import React, { useState, useEffect } from 'react';
import { generateQRMatrix } from '../utils/generators';
import { amountToWords } from '../utils/numberToWords';
import { computeTaxBreakdown } from '../utils/taxSchemes';
import { getStoredToken } from '../utils/authApi';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

// Same backend-proxied translate call BatchInvoiceGenerator.tsx already
// uses — replaces every direct-to-translate.googleapis.com fetch in this
// file. Direct calls fail two ways in production: (1) CORS — Google's
// endpoint sends no Access-Control-Allow-Origin header, so the browser
// blocks the response even on success; (2) 429s — a real invoice fires
// one fetch PER label/caption with no batching, so Google rate-limits
// almost immediately. The backend has no CORS restriction on itself and
// caches by {lang}:{text}, so repeated labels across many invoices don't
// refetch at all.
async function translateViaBackend(text: string, lang: string): Promise<string> {
  try {
    const res = await fetch(
      `${BACKEND}/translate?text=${encodeURIComponent(text)}&tl=${lang}`,
      { headers: getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {} }
    );
    if (!res.ok) return text;
    const data = await res.json();
    const result = data?.translated || text;
    return typeof result === 'string' ? result.replace(/\(?PropertyNameError\)?/gi, '').trim() : text;
  } catch {
    return text;
  }
}

// Batch translator: Translates an entire array in 1 single API call
async function translateBatchViaBackend(texts: string[], lang: string): Promise<string[]> {
  if (!texts.length) return [];
  try {
    const res = await fetch(`${BACKEND}/translate/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
      },
      body: JSON.stringify({ texts, tl: lang }),
    });
    if (!res.ok) return texts;
    const data = await res.json();
    return Array.isArray(data?.translations) ? data.translations : texts;
  } catch {
    return texts;
  }
}

// Universal Symbol, Shape & Glyph Mirroring (+, ★, •, ✦, ➕, emojis, etc.)
// Prevents (PropertyNameError) and mirrors leading/trailing shapes onto translated text
function cleanAndMirrorSymbols(engText: string, transText: string): string {
  if (!transText) return transText;
  let clean = transText.replace(/\(?PropertyNameError\)?/gi, '').replace(/\s{2,}/g, ' ').trim();

  const symbolRegex = /^([\s+\-*\/=<>%&|^~!?:;.,_#@$€£¥₹§©®™°•·★☆✓✔✕✖✗✦✧❖▲▼►◄◆◇○●□■\u2000-\u206F\u2700-\u27BF\uE000-\uF8FF\uD83C-\uDBFF\uDC00-\uDFFF\(\)\[\]\{\}]+)/u;
  const trailSymbolRegex = /([\s+\-*\/=<>%&|^~!?:;.,_#@$€£¥₹§©®™°•·★☆✓✔✕✖✗✦✧❖▲▼►◄◆◇○●□■\u2000-\u206F\u2700-\u27BF\uE000-\uF8FF\uD83C-\uDBFF\uDC00-\uDFFF\(\)\[\]\{\}]+)$/u;

  const leadSym = engText.match(symbolRegex)?.[1] || '';
  const trailSym = engText.match(trailSymbolRegex)?.[1] || '';

  if (leadSym || trailSym) {
    let core = clean;
    if (leadSym && core.startsWith(leadSym.trim())) core = core.slice(leadSym.trim().length);
    if (trailSym && core.endsWith(trailSym.trim())) core = core.slice(0, core.length - trailSym.trim().length);
    core = core.trim();
    if (core) {
      clean = `${leadSym}${core}${trailSym}`;
    }
  }
  return clean;
}

/**
 * BilingualInvoiceRenderer
 * ─────────────────────────
 * Used by QuickInvoiceView when a translation is active.
 * 
 * Rules:
 *  - If isBilingual=false or no arabicContent on elements → render pure English, zero layout change
 *  - If isBilingual=true + bilingualLayout='side'    → English left | Translation right (columns)
 *  - If isBilingual=true + bilingualLayout='stacked' → English on top, translation below (each block)
 *  - Arabic numerals only for lang='ar'
 *  - RTL font/direction only for RTL languages
 */

interface LineItem {
  id: string;
  description: string;
  qty: number;
  rate: number;
  arabicName?: string;
  taxRate?: number;
  taxName?: string;
  b2bTaxRate?: number;
  b2bTaxName?: string;
  taxInclusive?: boolean;
  // Transliterated/translated value per custom column key (Batch No,
  // Supplier, etc.) — keyed the same as the column's `key`.
  __customAr?: Record<string, string>;
}

interface TaxLine { label: string; rate: number; }
interface DiscountLine { label: string; rate: number; }

export interface BilingualInvoiceRendererProps {
  template: any;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;
  buyerTaxId?: string;
  productTaxName?: string;
  taxBreakdown?: { name: string; rate: number; amount: number }[];
  invoiceDate: string;
  invoiceTime: string;
  notes: string;
  lineItems: LineItem[];
  taxLines: TaxLine[];
  discountLines: DiscountLine[];
  autoDiscountAmount?: number;
  autoDiscountLabel?: string;
  autoDiscountLabelAr?: string;
  sym: string;
  shippingCost: number;
  invoiceStatus?: string;
  translateTargetLang?: string;
  isBilingual?: boolean;
  isB2B?: boolean;
  bilingualLayout?: 'side' | 'stacked'; // default: 'side'
  isThermal?: boolean;
  isThermal58?: boolean;
  showAmountInWords?: boolean;
  currencyCode?: string;
  amountInWordsTranslated?: string;
  amountInWordsStyle?: { fontFamily: string; fontSize: number; textAlign: 'left'|'center'|'right'; fontStyle: 'normal'|'italic'; fontWeight: 'normal'|'bold'; color: string; };
  savedElements?: any[] | null;
  totalsLabels?: { subtotal: string; total: string; tax: string; discount: string; shipping: string };
  showTrackingQR?: boolean; // caller-controlled — defaults to false since most callers (Quick Invoice) have no tracking feature
  autoQrPosition?: 'left' | 'center' | 'right';
  zoom?: number;
  // Staff/pharmacist names for Cashier:/Waiter:/Barista:/Pharmacist:/Stylist:/Staff:
  // template fields. Mirrors the patch QuickInvoiceView applies at
  // save-time — without these the live bilingual preview shows stale
  // template placeholder names instead of the real staff member.
  cashierName?: string;
  pharmacistName?: string;
  cashierNameTranslit?: string;
  pharmacistNameTranslit?: string;
  onQrMouseDown?: (e: React.MouseEvent, el: any) => void;
  // Free x/y drag offset for the invoice QR, driven by mobile's
  // touch-based drag handler in MobileInvoicePreview.tsx. Desktop mouse
  // drag doesn't need this (onQrMouseDown handles that case directly).
  qrOffset?: { x: number; y: number };
  // Values typed into the "Template Fields" sidebar in QuickInvoiceView
  // (Patient, Age, Power R, PD, Months Ready, etc). Without these, any
  // template that uses [Bracket] / __ / {Curly} placeholders shows the
  // raw placeholder text once bilingual mode replaces the CanvasWorkspace
  // preview with this renderer — CanvasWorkspace's buildCwElements()
  // patches them in, but this component previously had no equivalent step.
  invoiceNumber?: string;
  notesTranslated?: string;
  dynamicValues?: Record<string, string>;
  // Transliterated counterpart of dynamicValues, same keys — used to
  // patch the Arabic-side line instead of the raw English value.
  dynamicValuesAr?: Record<string, string>;
  // Translated label text for each dynamic field key (and invoice_number/
  // date/due_date), e.g. { patient: 'المريض', age: 'العمر' }. Used to
  // REBUILD multi-field Arabic lines from scratch, since Google Translate
  // collapses the double-space separators between fields when translating
  // the whole line, leaving no reliable boundary to splice into.
  dynamicFieldLabelsAr?: Record<string, string>;
  // Enables a named GST/VAT/QST breakdown (CGST/SGST/IGST/QST/etc.) instead
  // of a blended "Total Tax (X%)" line — pass the object returned by
  // getDefaultSchemeForCountryAndRegion(sellerCountry, sellerRegion), plus
  // the same seller/buyer region strings used to compute it. Omit for
  // invoices with no saved tax-scheme context; the component falls back
  // to the old blended line.
  taxScheme?: any;
  sellerRegion?: string;
  buyerRegion?: string;
  // Transliterated component names (CGST -> سي جي إس تي, etc.), keyed by
  // the English name — same treatment POSReceiptRenderer.taxBreakdownAr
  // gets. These are acronyms, not dictionary words, so translateText just
  // echoes them back; the caller must transliterate instead.
  taxBreakdownAr?: Record<string, string>;
  // Pre-translated QR captions, computed by the caller via a
  // backend-proxied /translate call. Takes priority over this
  // component's own internal autoQrLabels fetch, which hits Google's
  // translate endpoint directly from the browser and silently fails
  // inside a React Native WebView (CORS/network restrictions) — that's
  // why the QR caption stayed English-only on mobile while everything
  // else translated fine.
  qrCaptionInvoiceAr?: string;
  qrCaptionTrackingAr?: string;
}

// ─── Language helpers ─────────────────────────────────────────────────────────

// RTL languages — script-based, not just "is Arabic".
// NOTE: 'ku' (Kurdish Kurmanji) was previously in this list but Kurmanji is
// normally written in LATIN script (LTR) in Turkey/Syria — that looks like a
// pre-existing bug. Sorani Kurdish ('ckb'), which IS Arabic-script/RTL, was
// already correctly included separately. I removed 'ku' and added 'ug'
// (Uyghur, Arabic script) and 'yi' (Yiddish, Hebrew script), which were
// missing. Flagging this in case 'ku' being RTL was intentional/relied-upon.
const RTL_LANGS = ['ar', 'ur', 'fa', 'iw', 'he', 'ckb', 'ps', 'sd', 'dv', 'ug', 'yi'];
const isRTL = (lang?: string) => RTL_LANGS.includes(lang || '');

const arNum = (s: string) => s.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]);

const ARABIC_CHAR_RE = /[\u0600-\u06FF]/;

// Bidi-isolates each line of translated content individually. Pure-Latin
// lines (email, phone, address fragments left untranslated on purpose)
// get forced LTR direction so the browser's bidi algorithm doesn't
// reorder them when they sit inside an RTL container — same protection
// BiNum already applies to numbers, just line-by-line here since a
// multi-line block can mix Arabic and Latin-only lines.
function renderBidiSafeLines(text: string, rtl: boolean, lang?: string) {
  return text.split('\n').map((line, i) => {
    const isLatinOnly = !ARABIC_CHAR_RE.test(line);
    const isIdentifier = /[@]|https?:\/\//i.test(line);
    // ⚡ Eastern Arabic-Indic digits apply strictly when lang === 'ar'
    const content = (lang === 'ar') && !isIdentifier
      ? line.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d])
      : line;
    return (
      <div
        key={i}
        style={
          isLatinOnly
            ? { direction: 'ltr', unicodeBidi: 'embed', textAlign: rtl ? 'right' : 'left' }
            : undefined
        }
      >
        {content}
      </div>
    );
  });
}

const SCRIPT_FONTS: Record<string, string> = {
  ar: '"Noto Sans Arabic", Arial, sans-serif',
  ur: '"Noto Sans Arabic", Arial, sans-serif',
  fa: '"Noto Sans Arabic", Arial, sans-serif',
  ps: '"Noto Sans Arabic", Arial, sans-serif',
  sd: '"Noto Sans Arabic", Arial, sans-serif',
  ug: '"Noto Sans Arabic", Arial, sans-serif',
  ckb: '"Noto Sans Arabic", Arial, sans-serif',
  hi: '"Noto Sans Devanagari", Arial, sans-serif',
  mr: '"Noto Sans Devanagari", Arial, sans-serif',
  ne: '"Noto Sans Devanagari", Arial, sans-serif',
  sa: '"Noto Sans Devanagari", Arial, sans-serif',
  bho: '"Noto Sans Devanagari", Arial, sans-serif',
  mai: '"Noto Sans Devanagari", Arial, sans-serif',
  doi: '"Noto Sans Devanagari", Arial, sans-serif',
  bn: '"Noto Sans Bengali", Arial, sans-serif',
  as: '"Noto Sans Bengali", Arial, sans-serif',
  ta: '"Noto Sans Tamil", Arial, sans-serif',
  te: '"Noto Sans Telugu", Arial, sans-serif',
  kn: '"Noto Sans Kannada", Arial, sans-serif',
  ml: '"Noto Sans Malayalam", Arial, sans-serif',
  gu: '"Noto Sans Gujarati", Arial, sans-serif',
  pa: '"Noto Sans Gurmukhi", Arial, sans-serif',
  or: '"Noto Sans Oriya", Arial, sans-serif',
  si: '"Noto Sans Sinhala", Arial, sans-serif',
  dv: '"Noto Sans Thaana", Arial, sans-serif',
  th: '"Noto Sans Thai", Arial, sans-serif',
  lo: '"Noto Sans Lao", Arial, sans-serif',
  km: '"Noto Sans Khmer", Arial, sans-serif',
  my: '"Noto Sans Myanmar", Arial, sans-serif',
  zh: '"Noto Sans SC", Arial, sans-serif',
  'zh-TW': '"Noto Sans TC", Arial, sans-serif',
  ja: '"Noto Sans JP", Arial, sans-serif',
  ko: '"Noto Sans KR", Arial, sans-serif',
  am: '"Noto Sans Ethiopic", Arial, sans-serif',
  ti: '"Noto Sans Ethiopic", Arial, sans-serif',
  he: '"Noto Sans Hebrew", Arial, sans-serif',
  iw: '"Noto Sans Hebrew", Arial, sans-serif',
  yi: '"Noto Sans Hebrew", Arial, sans-serif',
  ka: '"Noto Sans Georgian", Arial, sans-serif',
  hy: '"Noto Sans Armenian", Arial, sans-serif',
};

const getTransFont = (lang?: string) => SCRIPT_FONTS[lang || ''] || 'Inter, Arial, sans-serif';

const DEFAULT_TOTAL_LABELS = { subtotal: 'Subtotal', total: 'TOTAL', tax: 'Tax', discount: 'Discount', shipping: 'Shipping' };

// ── 100% Dynamic Totals Translator (Batch API translates all 5 words in 1 call) ──
async function translateTotalLabels(lang: string) {
  const words = ['Subtotal', 'TOTAL', 'Tax', 'Discount', 'Shipping'];
  const results = await translateBatchViaBackend(words, lang);
  if (Array.isArray(results) && results.length === 5) {
    const [subtotal, total, tax, discount, shipping] = results;
    return {
      subtotal: subtotal || 'Subtotal',
      total: total || 'TOTAL',
      tax: tax || 'Tax',
      discount: discount || 'Discount',
      shipping: shipping || 'Shipping',
    };
  }
  return DEFAULT_TOTAL_LABELS;
}
// Rebuilds the translated line's VALUE from the live English value,
// keeping only the translated LABEL from the stored arabicContent.
// Fixes staleness for Invoice #, Date, Due Date, PO # on bilingual PDFs.
const IDENTIFIER_FIELD_RE =
  /^(Invoice #|Bill|Date|Due Date|PO #)\s*:\s*(.+)$/i;
const COLON_RE = /[:：]/; // ASCII colon + CJK fullwidth colon

// Splits a line like "Patient: amnah    Age: 24" (or its Arabic
// equivalent) into ordered [label, value] segments on 2+ space
// boundaries between fields — same separator convention already used
// elsewhere in this codebase's multi-field-per-line detection.
function splitFieldSegments(line: string): { label: string; value: string }[] {
  return line
    .split(/\s{2,}(?=\S)/)
    .map(seg => {
      const idx = seg.search(/[:：]/);
      if (idx === -1) return null;
      return { label: seg.slice(0, idx + 1), value: seg.slice(idx + 1).trim() };
    })
    .filter((s): s is { label: string; value: string } => !!s);
}

const ltrIsolate = (s: string) => `\u2066${s}\u2069`;

function patchElArabicText(
  el: any,
  patchedEngText: string,
  dynamicValues?: Record<string, string>,
  dynamicValuesAr?: Record<string, string>,
  dynamicFieldLabelsAr?: Record<string, string>,
  lang?: string,
  invoiceNumber?: string,
  cashierName?: string,
  cashierNameTranslit?: string,
  pharmacistName?: string,
  pharmacistNameTranslit?: string
): string {
  let arabicText = el.arabicContent || '';
  if (!arabicText) return arabicText;
  arabicText = arabicText.replace(/\(?PropertyNameError\)?/gi, '').trim();

  // ⚡ Keep the customer card Arabic side clean; do not re-scramble it
  if (el.id === 'auto-injected-client-box') {
    return arabicText;
  }

  // Direct transliterated replacement for {staff_name} on Arabic side
  if (cashierName) {
    const arStaff = cashierNameTranslit || cashierName;
    arabicText = arabicText.replace(/\{staff_name\}/gi, ltrIsolate(arStaff));
  }

  const engLines = patchedEngText.split('\n');
  const arLines = arabicText.split('\n');

  const targetLabelRegex = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|guest|member|subscriber|attn|ship\s*to|deliver\s*to)\s*:/i;
  const firstEngLine = (engLines[0] || '').trim();
  const hasClientHeader = targetLabelRegex.test(firstEngLine);
  const candName = (dynamicValues?.client_name || dynamicValues?.clientName || '').trim().toLowerCase();

  // Universal Customer Detector: catches "Bill To:", name on line 1, auto box, or blocks with email + phone
  const isClientTarget =
    hasClientHeader ||
    el.id === 'auto-injected-client-box' ||
    (patchedEngText.includes('Email:') && patchedEngText.includes('Phone:')) ||
    (patchedEngText.includes('@') && /[\d\+\-\(\)\s]{7,}/.test(patchedEngText)) ||
    (Boolean(candName) && firstEngLine.toLowerCase() === candName);

  if (isClientTarget) {
    const labelHeader = hasClientHeader ? (arLines[0] || firstEngLine) : '';
    const startIndex = hasClientHeader ? 1 : 0;

    // Dynamically match each line after the header (or line 0 if no header)
    const restLines = engLines.slice(startIndex).map((engLine, i) => {
      const trimmed = engLine.trim();
      if (!trimmed) return '';

      // 1. Email: Dynamically translate label from dynamicFieldLabelsAr (Zero hardcoding)
      if (/[@]/i.test(trimmed)) {
        const val = trimmed.includes(':') ? trimmed.slice(trimmed.indexOf(':') + 1).trim() : trimmed;
        const emailLabel = dynamicFieldLabelsAr?.email || dynamicFieldLabelsAr?.['Email'] || (trimmed.includes(':') ? trimmed.slice(0, trimmed.indexOf(':')).trim() : 'Email');
        return `${emailLabel}: ${val}`;
      }

      // 2. Phone: Dynamically translate label + Eastern Arabic-Indic numerals strictly when lang === 'ar'
      if (/^(phone|tel|mobile|mob|contact|cell)\s*:/i.test(trimmed) || /^[\d\s+\-().]{7,}$/.test(trimmed)) {
        const val = trimmed.includes(':') ? trimmed.slice(trimmed.indexOf(':') + 1).trim() : trimmed;
        const phoneLabel = dynamicFieldLabelsAr?.phone || dynamicFieldLabelsAr?.['Phone'] || (trimmed.includes(':') ? trimmed.slice(0, trimmed.indexOf(':')).trim() : 'Phone');
        const formattedPhone = lang === 'ar' ? arNum(val) : val;
        return `${phoneLabel}: ${formattedPhone}`;
      }

      // 3. Tax ID Line: Dynamically translate label from dynamicFieldLabelsAr (Zero hardcoding)
      if (/^(tax\s*id|gstin|vat|trn|npwp)\s*:/i.test(trimmed)) {
        const val = trimmed.slice(trimmed.indexOf(':') + 1).trim();
        const taxLabel = dynamicFieldLabelsAr?.tax_id || dynamicFieldLabelsAr?.taxId || dynamicFieldLabelsAr?.['Tax ID'] || 'Tax ID';
        return `${taxLabel}: ${val}`;
      }

      // 4. Custom "Label: value" lines (STORE ID, PERIOD, etc.)
      if (/^[^:@]{1,30}:\s*\S/.test(trimmed) && !/^(attention|attn)\s*:/i.test(trimmed)) {
        const engColon = trimmed.indexOf(':');
        const engKey = trimmed.slice(0, engColon).trim().toLowerCase();
        
        const matchingArLine = arLines.find(al => {
          const arColon = al.indexOf(':') !== -1 ? al.indexOf(':') : al.indexOf('：');
          if (arColon !== -1) {
            const arKey = al.slice(0, arColon).trim();
            if (dynamicFieldLabelsAr && dynamicFieldLabelsAr[engKey] === arKey) return true;
          }
          return false;
        });
        if (matchingArLine) return matchingArLine;

        const engVal = trimmed.slice(engColon + 1).trim();
        const byValue = arLines.find(al => COLON_RE.test(al) && al.includes(engVal));
        if (byValue) return byValue;

        return arLines[i + 1] || engLine;
      }

      // 5. Client name fallback
      if (dynamicValuesAr?.clientName && i === 0 && !trimmed.includes(':')) {
        return dynamicValuesAr.clientName;
      }

      // 6. Safe Address / General Text Fallback:
      // Never grab a phone number, email, or tax ID from arLines to display as an address!
      const isTaxIdentifier = (s: string) =>
        /(tax\s*id|vat\s*no|gstin|trn|npwp|nif|cif|inn)/i.test(s) ||
        /\b[A-Z0-9]{8,}\b/.test(s);

      const candidateAr = arLines[i + 1];
      if (candidateAr) {
        const candTrim = candidateAr.trim();
        const isCandPhone = /^[\d\s+\-().]{7,}$/.test(candTrim);
        const isCandEmail = candTrim.includes('@');
        const isCandTax = isTaxIdentifier(candTrim);

        if (!isCandPhone && !isCandEmail && !isCandTax) {
          return candidateAr;
        }
      }

      // Check if arLines has an address line (non-phone, non-email, non-tax, non-name)
      const addressCandidate = arLines.slice(1).find(al => {
        const alt = al.trim();
        if (!alt) return false;
        if (/^[\d\s+\-().]{7,}$/.test(alt)) return false;
        if (alt.includes('@')) return false;
        if (isTaxIdentifier(alt)) return false;
        if (alt === arLines[1]) return false;
        return true;
      });

      if (addressCandidate) return addressCandidate;

      // While translation is fetching, show the live English address — NEVER duplicate the phone number!
      return engLine;
    });

    return labelHeader ? [labelHeader, ...restLines].join('\n') : restLines.join('\n');
  }

  const dynEntries = Object.entries({
  ...(dynamicValuesAr || {}),
  ...(dynamicValues || {}),
}).filter(([, v]) => !!v);

  const patchedArLines = arLines.map((arLine, i) => {
    const engLine = engLines[i];
    if (!engLine) return arLine;

    const identifierSegments = splitFieldSegments(engLine);
    const m = identifierSegments.length <= 1 ? engLine.match(IDENTIFIER_FIELD_RE) : null;
    if (m) {
  const liveValue =
    /^(Invoice #|Bill)$/i.test(m[1])
      ? (invoiceNumber || m[2])
      : m[2];

  const colonMatch = arLine.match(COLON_RE);
  if (!colonMatch || colonMatch.index === undefined) return arLine;

  const label = arLine.slice(0, colonMatch.index + 1);

  const normalizeFieldKey = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const englishFieldKey = normalizeFieldKey(m[1]);

  const matchedKey = Object.keys(dynamicValues || {}).find(
    key => normalizeFieldKey(key) === englishFieldKey
  );

  const arabicValue =
    matchedKey && dynamicValuesAr?.[matchedKey] != null
      ? dynamicValuesAr[matchedKey]
      : liveValue;

  // ⚡ Eastern Arabic numerals strictly when lang === 'ar'
  const finalValue = (lang === 'ar')
    ? arNum(String(arabicValue))
    : String(arabicValue);

  return `${label} ${finalValue}`;
}

    // Template Fields (Patient, Age, Power R/L/PD, etc.) — a single
    // template line can carry SEVERAL "Label: value" fields separated by
    // 2+ spaces (e.g. "Power R: tree  L: love  PD: 10000"). The Arabic
    // label text is already translated, so we can't match by English
    // label text like patchDynamicFieldsLocal does. Split both the
    // patched English line and the Arabic line into ordered field
    // segments on the same 2+ space boundary, and replace each Arabic
    // segment's value (keeping its translated label) with the
    // transliterated value for whichever dynamic field landed in that
    // position on the English side. This only fires when segment counts
    // match on both sides — if translation reflowed the line into a
    // different number of segments, we fall through to the old
    // single-field behavior below rather than guessing wrong.
    // Generic Multi-Field Preservation (ZERO HARDCODING):
    // If the AI already translated this line, KEEP the AI's translated line intact!
    // Only patch dynamic values (e.g. staff name, patient name) into it if present.
    if (arLine && arLine.trim()) {
      let patchedLine = arLine;

      // 1. Patch staff name into the translated line (NEVER touch lines with email addresses!)
      if (!engLine.includes('@') && cashierName && new RegExp(`\\b${cashierName}\\b`, 'i').test(engLine)) {
        const staffVal = cashierNameTranslit || cashierName;
        patchedLine = patchedLine.replace(new RegExp(`\\b${cashierName}\\b`, 'gi'), isRTL(lang) ? ltrIsolate(staffVal) : staffVal);
      }

      // 2. Patch any dynamic values that appear on this line
      for (const [key, value] of dynEntries) {
        if (value && engLine.includes(value)) {
          const transVal = dynamicValuesAr?.[key] || value;
          const finalVal = isRTL(lang)
            ? ltrIsolate(lang === 'ar' ? arNum(String(transVal)) : String(transVal))
            : String(transVal);
          patchedLine = patchedLine.replace(new RegExp(value, 'gi'), finalVal);
        }
      }

      return patchedLine;
    }

    // Fallback — single dynamic field occupies the whole line.
    for (const [key, value] of dynEntries) {
      if (!engLine.includes(value)) continue;
      const colonMatch = arLine.match(COLON_RE);
      if (!colonMatch || colonMatch.index === undefined) continue;
      const label = arLine.slice(0, colonMatch.index + 1);

      // Check if arLine already has translated content after the colon
      const existingTrans = arLine.slice(colonMatch.index + 1).trim();
      const hasTranslatedContent = existingTrans && existingTrans.toLowerCase() !== value.toLowerCase();

      let transValue = dynamicValuesAr?.[key];
      if (!transValue && hasTranslatedContent) {
        // Never overwrite already-translated content with raw English!
        return arLine;
      }

      const valToUse = transValue || value;

      // RTL languages (Arabic, Hebrew, Urdu, Persian) wrap in LTR isolate so numbers/dates don't flip
      const finalValue = isRTL(lang)
        ? `\u2066${lang === 'ar' ? arNum(String(valToUse)) : String(valToUse)}\u2069`
        : String(valToUse);

      return `${label} ${finalValue}`;
    }

    return arLine;
  });

  return patchedArLines.join('\n');
}
// ─── Section extractor (same logic as POSReceiptRenderer) ─────────────────────
function patchElText(el: any, ctx: {
  clientName: string; clientEmail: string; clientPhone: string;
  clientAddress: string; buyerTaxId?: string; invoiceDate: string; invoiceTime: string; notes: string;
  cashierName?: string; pharmacistName?: string;
  dynamicValues?: Record<string, string>;
  invoiceNumber?: string;
}): string {
  let t = el.textContent || '';
  if (/[\u0600-\u06FF]/.test(t)) return t; // skip Arabic elements

  // ⚡ Keep the customer card as-is; do not let patchElText modify it or re-expand other lines
  if (el.id === 'auto-injected-client-box') {
    return t;
  }
  const { clientName, clientEmail, clientPhone, clientAddress, buyerTaxId, invoiceDate, invoiceTime, notes, cashierName, pharmacistName, dynamicValues, invoiceNumber } = ctx;

  if (invoiceNumber) {
    t = t.replace(/\b(Invoice #|Bill No\.?|Receipt No\.?|Bill|Receipt|Order)\s*:\s*[^\n]*/i, (_, l) => `${l}: ${invoiceNumber}`);
    t = t.replace(/\{invoice_number\}/g, invoiceNumber);
  }

  const targetLabelRegex = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|guest|member|subscriber|attn|ship\s*to|deliver\s*to)\s*:/i;
  const originalLines = t.split('\n');
  const firstLine = (originalLines[0] || '').trim();
  const hasClientHeader = targetLabelRegex.test(firstLine);

  const isClientTarget =
    hasClientHeader ||
    el.id === 'auto-injected-client-box' ||
    (t.includes('Email:') && t.includes('Phone:')) ||
    (t.includes('@') && /[\d\+\-\(\)\s]{7,}/.test(t)) ||
    (Boolean(clientName) && firstLine.toLowerCase() === clientName.trim().toLowerCase());

  if (isClientTarget) {
    const headerLabel = hasClientHeader ? (firstLine.split(':')[0] ? `${firstLine.split(':')[0]}:` : 'Bill To:') : '';
    
    // Identify non-contact custom lines (e.g. STORE ID: ..., PERIOD: ...) to preserve them
    const isStandardContactLine = (line: string) => {
      const trimmed = line.trim();
      if (!trimmed) return true;
      if (targetLabelRegex.test(trimmed)) return true;
      if (/^(tax\s*id|vat\s*no|gstin|trn)\s*:/i.test(trimmed)) return true;
      if (/^(phone|tel|mobile|mob|contact|cell)\s*:/i.test(trimmed)) return true;
      if (/^(email|e-mail|mail)\s*:/i.test(trimmed)) return true;
      if (/^(address|adress|addr)\s*[\:\-]+/i.test(trimmed)) return true;
      if (/^attention\s*:/i.test(trimmed)) return true;
      return false;
    };

    const preservedCustomLines: string[] = [];
    originalLines.slice(1).forEach((line, idx) => {
      // If line 1 is just the old client name, don't keep it as custom
      if (idx === 0 && !line.includes(':')) return;
      // Only real "Label: value" lines are custom. Bare email/phone/address lines
      // were being kept AND re-added above, duplicating them.
      if (!isStandardContactLine(line) && /^[^:@]{1,30}:\s*\S/.test(line.trim())) {
        preservedCustomLines.push(line);
      }
    });

    const newBillToLines: string[] = headerLabel ? [headerLabel] : [];
    if (clientName?.trim()) {
      newBillToLines.push(clientName.trim());
    }
    if (buyerTaxId?.trim() && !/[\/\\<>{}\[\]()=;]/.test(buyerTaxId)) {
      newBillToLines.push(`Tax ID: ${buyerTaxId.trim()}`);
    }
    if (clientAddress?.trim()) {
      newBillToLines.push(clientAddress.trim());
    }
    if (clientEmail?.trim() && clientEmail.includes('@')) {
      newBillToLines.push(clientEmail.trim());
    }
    if (clientPhone?.trim()) {
      newBillToLines.push(clientPhone.trim());
    }

    const userAttn = dynamicValues?.attention || dynamicValues?.attn;
    if (userAttn?.trim()) {
      newBillToLines.push(`Attention: ${userAttn.trim()}`);
    }

    // Append all preserved custom lines (STORE ID, PERIOD, etc.)
    if (preservedCustomLines.length > 0) {
      newBillToLines.push(...preservedCustomLines);
    }

    t = newBillToLines.join('\n');
  }

  if (clientPhone) t = t.replace(/\b(Phone|Tel|Mobile|Mob|Contact|Cell)\s*:\s*[^\n]*/gi, `$1: ${clientPhone}`);
  if (clientEmail) t = t.replace(/\b(Email|E-mail|Mail)\s*:\s*[^\n]*/gi, `$1: ${clientEmail}`);
  if (clientAddress) t = t.replace(/\b(Address|Adress|Addr)\s*[\:\-]+\s*[^\n]*/gi, (_, l) => `${l}: ${clientAddress}`);
  if (invoiceDate) t = t.replace(/(?<!Due\s)Date:\s*[\d]{1,4}[\/\-\.][\d]{1,2}[\/\-\.][\d]{2,4}/gi, `Date: ${invoiceDate}`);
  if (invoiceDate) t = t.replace(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/g, invoiceDate);
  if (invoiceTime) t = t.replace(/\b\d{1,2}:\d{2}\s*(?:am|pm)\b/gi, invoiceTime);
  if (notes && (t.toLowerCase().startsWith('notes:') || t.toLowerCase().startsWith('terms:'))) t = `Notes:\n${notes}`;

  if (cashierName) {
    t = t.replace(/\{staff_name\}/gi, cashierName);
  }
  
  t = patchDynamicFieldsLocal(t, dynamicValues);
  return t;
}
// Mirrors patchDynamicFields() in QuickInvoiceView.tsx / App.tsx —
// duplicated here rather than imported to avoid a circular import
// (QuickInvoiceView imports this component). Keep both in sync if the
// placeholder-matching rules change.
function patchDynamicFieldsLocal(text: string, dynamicValues?: Record<string, string>): string {
  if (!dynamicValues) return text;
  let t = text;
  for (const [key, value] of Object.entries(dynamicValues)) {
    if (!value) continue;
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    t = t.replace(
      new RegExp(`\\{\\s*${escapedKey}\\s*\\}`, 'gi'),
      value
    );
    const labelPattern = key.replace(/_/g, '[\\s\\/\\.\\(\\)#]*');
    t = t.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*\\[[^\\]]*\\]`, 'gi'), (_, l) => `${l}: ${value}`);
    t = t.replace(new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*_{2,}`, 'gi'), (_, l) => `${l}: ${value}`);
    t = t.replace(
      new RegExp(`\\b(${labelPattern})\\b\\s*:\\s*([^\\n:]{0,50}?)(?=\\s{2,}[A-Za-z]|\\s+[A-Za-z][A-Za-z\\s]{1,15}:|\\n|$)`, 'gi'),
      (_, l) => `${l}: ${value}`
    );
  }
  return t;
}

function isScanCaption(el: any) {
  return el.type === 'text' && /^scan to/i.test((el.textContent || '').trim());
}

// Best-effort height for a canvas element when computing original gaps —
// real height isn't measured here (unlike the main PDF path), so this is
// a design-time approximation based on stored height or type defaults.
function estimateElHeight(el: any): number {
  if (el.height) return Math.min(el.height, 105);
  if (el.type === 'qr') return 100;
  if (el.type === 'image') return 80;
  if (el.type === 'shape') return 4;
  return 20;
}

function groupOverlappingShapes(els: any[]) {
  // Only treat shapes with height >= 20 as banners (ignore thin decorative lines)
  const shapes = els.filter(
    (el: any) =>
      el.type === 'shape' &&
      el.backgroundColor &&
      el.backgroundColor !== 'transparent' &&
      (el.height || 0) >= 20
  );
  const consumedIds = new Set<string>();
  const groups: { shape: any; children: any[] }[] = [];

  shapes.forEach((shape: any) => {
    const sTop = shape.y || 0, sBottom = sTop + (shape.height || 0);
    const sLeft = shape.x || 0, sRight = sLeft + (shape.width || 0);
    const children = els.filter((el: any) => {
      if (el.id === shape.id || consumedIds.has(el.id)) return false;
      const eTop = el.y || 0, eBottom = eTop + (el.height || 0);
      const eLeft = el.x || 0, eRight = eLeft + (el.width || 0);
      return eTop < sBottom && eBottom > sTop && eLeft < sRight && eRight > sLeft;
    });

    // Only create an overlap group if the shape actually contains text children
    if (children.length > 0) {
      children.forEach((c: any) => consumedIds.add(c.id));
      consumedIds.add(shape.id);
      groups.push({ shape, children });
    }
  });

  const remaining = els.filter((el: any) => !consumedIds.has(el.id));
  return { groups, remaining };
}

function renderOverlapGroup(
  shape: any,
  children: any[],
  getPatchedPair: (el: any) => { textContent: string; arabicContent: string },
  isBilingual: boolean,
  lang?: string
) {
  const rtl = isRTL(lang);

  // Sort children so Document Title ("INVOICE" / "SELLER FEE INVOICE") appears first,
  // followed by Company Name, Subtitle, and Invoice numbers/dates
  const sortedChildren = [...children].sort((a, b) => {
    const aIsTitle = /invoice|bill|receipt|tax invoice|seller fee/i.test(a.textContent || '');
    const bIsTitle = /invoice|bill|receipt|tax invoice|seller fee/i.test(b.textContent || '');
    if (aIsTitle && !bIsTitle) return -1;
    if (!aIsTitle && bIsTitle) return 1;

    // If they have different vertical levels, sort by Y
    if (Math.abs((a.y || 0) - (b.y || 0)) > 15) {
      return (a.y || 0) - (b.y || 0);
    }
    // If on the same horizontal line, left-side elements come before right-side elements
    return (a.x || 0) - (b.x || 0);
  });

  return (
    <div
      key={shape.id}
      style={{
        width: '100%',
        minHeight: shape.height || 80,
        height: 'auto', // Expands dynamically so text never overflows
        backgroundColor: shape.backgroundColor,
        borderRadius: shape.borderRadius || 0,
        padding: '14px 18px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        marginBottom: 8,
      }}
    >
      {sortedChildren.map((child: any) => {
        if (child.type !== 'text') {
          return (
            <div key={child.id} style={{ color: child.color || '#fff' }}>
              {child.textContent}
            </div>
          );
        }

        const { textContent, arabicContent: rawArabic } = getPatchedPair(child);
        const arabicContent = cleanAndMirrorSymbols(textContent, rawArabic);
        const hasTranslation = isBilingual && arabicContent && arabicContent !== textContent;

        const baseStyle: React.CSSProperties = {
          fontSize: child.fontSize || 12,
          fontWeight: child.fontWeight || 'normal',
          fontStyle: child.fontStyle || 'normal',
          lineHeight: child.lineHeight || 1.35,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: child.color || '#fff',
        };

        if (!hasTranslation) {
          return (
            <div
              key={child.id}
              style={{
                ...baseStyle,
                fontFamily: child.fontFamily || 'Inter, Arial, sans-serif',
                textAlign: (child.textAlign as any) || 'left',
              }}
            >
              {textContent}
            </div>
          );
        }

        // Bilingual: clean row with English Left (50%) and Arabic Right (50%)
        return (
          <div
            key={child.id}
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {/* English (Left 50%) */}
            <div
              style={{
                flex: '1 1 50%',
                minWidth: 0,
                maxWidth: '50%',
                ...baseStyle,
                fontFamily: child.fontFamily || 'Inter, Arial, sans-serif',
                textAlign: child.textAlign === 'center' ? 'left' : ((child.textAlign as any) || 'left'),
              }}
            >
              {textContent}
            </div>

            {/* Arabic (Right 50%) */}
            <div
              style={{
                flex: '1 1 50%',
                minWidth: 0,
                maxWidth: '50%',
                ...baseStyle,
                fontFamily: getTransFont(lang),
                direction: rtl ? 'rtl' : 'ltr',
                textAlign: rtl ? 'right' : 'left',
                opacity: 0.95,
                fontSize: Math.round((child.fontSize || 12) * 0.9),
              }}
            >
              {renderBidiSafeLines(arabicContent, rtl, lang)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function renderElList(els: any[], renderEl: (el: any, captionEl?: any) => any, startY?: number) {
  const used = new Set<number>();
  // First pass: for every QR, find the nearest unused scan-caption below it
  const pairing = new Map<number, number>(); // qrIndex -> captionIndex
  els.forEach((el: any, i: number) => {
    if (el.type !== 'qr') return;
    // If QR already owns a bundled label, do not pair an external caption
    if (el.showLabel !== false && el.label) return;

    let bestIdx = -1;
    let bestDist = Infinity;
    els.forEach((cand: any, j: number) => {
      if (used.has(j) || !isScanCaption(cand)) return;
      const dist = Math.abs((cand.y || 0) - (el.y || 0));
      if (dist < bestDist) { bestDist = dist; bestIdx = j; }
    });
    if (bestIdx !== -1) {
      pairing.set(i, bestIdx);
      used.add(bestIdx);
    }
  });

  // Build the ordered list of render units — a QR+caption pair collapses
  // into one unit so the pairing above stays intact.
  const units: { i: number; y: number; bottom: number; captionIdx?: number }[] = [];
  els.forEach((el: any, i: number) => {
    if (used.has(i) && !pairing.has(i)) return; // caption consumed by a QR, skip
    if (el.type === 'qr' && pairing.has(i)) {
      const capIdx = pairing.get(i)!;
      const cap = els[capIdx];
      const bottom = Math.max((el.y || 0) + estimateElHeight(el), (cap.y || 0) + estimateElHeight(cap));
      units.push({ i, y: el.y || 0, bottom, captionIdx: capIdx });
      return;
    }
    units.push({ i, y: el.y || 0, bottom: (el.y || 0) + estimateElHeight(el) });
  });

  // Preserve each unit's ORIGINAL canvas gap from whatever precedes it —
  // startY (e.g. the table's bottom) for the first unit in a footer list,
  // or flush (0) for the very first thing on the page when no startY is
  // given. Without this every element collapsed to the same flat 2–6px
  // margin regardless of how far apart the customer actually placed them.
  //
  // CAP on the very first gap: startY is the DESIGN-TIME estimated table
  // bottom (tableEl.y + tableEl.height), and that saved height already
  // bakes in calcTableHeight()'s totalsBoxHeight guess. This component
  // renders its own real Totals/Status-Stamp/Notes blocks as separate,
  // naturally-flowed DOM elements BETWEEN the table and this footer list
  // — so that estimated leading gap double-counts space already consumed
  // for real, producing a large dead zone whenever the estimate overshoots
  // actual content (exactly what happened on bilingual A4 invoices with
  // few rows/small totals). Clamp it so a bad estimate can't blow up the
  // layout; the elements after the first still use their real relative
  // spacing.
  const MAX_LEADING_FOOTER_GAP = 24;
  let prevBottom = startY !== undefined ? startY : (units[0]?.y ?? 0);
  return units.map((u, idx) => {
    let gap = (prevBottom === (startY ?? units[0]?.y ?? 0) && u === units[0] && startY === undefined)
      ? 0
      : Math.max(0, Math.round(u.y - prevBottom));
    if (idx === 0 && startY !== undefined) {
      gap = Math.min(gap, MAX_LEADING_FOOTER_GAP);
    }
    prevBottom = u.bottom;
    const el = els[u.i];
    const cap = u.captionIdx !== undefined ? els[u.captionIdx] : undefined;
    return (
      <div key={el.id || u.i} style={{ marginTop: gap }}>
        {renderEl(el, cap)}
      </div>
    );
  });
}
function extractSections(template: any, savedElements?: any[] | null) {
  const elements = (template?.elements || []).sort((a: any, b: any) => (a.y || 0) - (b.y || 0));
  const tableEl = elements.find((el: any) => el.type === 'table');
  const tableY = tableEl ? tableEl.y : 999999;
  let headerEls = elements.filter((el: any) => (el.y || 0) < tableY && el.type !== 'table');
  const footerEls = elements.filter((el: any) => (el.y || 0) > tableY && el.type !== 'table');

  // Header text (Invoice #, Date, Due Date, PO #, Bill To, etc.) must
  // reflect the fully-patched savedElements, not the raw/pre-save
  // translatedTemplate this component receives as `template` — that raw
  // template still has design-time sample values baked in (e.g.
  // "INV-2026-904"), and nothing else in this component ever patches
  // them. Swap in the matching savedElements entry (by id) wherever one
  // exists; fall back to the template's own version otherwise.
  if (savedElements && savedElements.length > 0) {
    const savedById = new Map(savedElements.map((el: any) => [el.id, el]));
    headerEls = headerEls.map((el: any) => savedById.get(el.id) || el);
  }

  // ⚡ DEDUPLICATE CUSTOMER BLOCKS:
  // If a styled customer card (auto-injected-client-box or border-left box) is present,
  // remove any unstyled duplicate customer text block above it
  const hasCard = headerEls.some((el: any) => el.id === 'auto-injected-client-box' || (el.borderLeftWidth && el.borderLeftWidth > 0));
  if (hasCard) {
    const targetLabelRegex = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|guest|member|subscriber|attn|ship\s*to|deliver\s*to)\s*:/i;
    headerEls = headerEls.filter((el: any) => {
      if (el.id === 'auto-injected-client-box' || (el.borderLeftWidth && el.borderLeftWidth > 0)) return true;
      const txt = (el.textContent || '').trim();
      const firstLine = (txt.split('\n')[0] || '').trim();
      const isStoreHeader = /(gstin|fssai|tax invoice|store|retail|restaurant|cuisine|table:\s*\d)/i.test(txt);
      if (isStoreHeader) return true;

      // If a styled card is present, any other client block (Bill To:, Client:, Tax ID:, etc.) is redundant
      const isDuplicateClientBlock =
        targetLabelRegex.test(firstLine) ||
        txt.toLowerCase().startsWith('bill to:') ||
        txt.toLowerCase().startsWith('client:') ||
        txt.toLowerCase().startsWith('customer:') ||
        txt.toLowerCase().startsWith('patient:') ||
        txt.toLowerCase().includes('{client_details}') ||
        ((txt.toLowerCase().includes('tax id:') || txt.toLowerCase().includes('tax id :') || txt.toLowerCase().includes('npwp')) && !isStoreHeader);

      return !isDuplicateClientBlock;
    });
  }

  return { headerEls, tableEl, footerEls };
}

const isClientCardText = (t: string) =>
  /\b(tax id|email|phone|address)\b/i.test(t || '') && (t || '').includes('\n');

function TextBlock({
  el, lang, isBilingual, layout, isThermal = false,
}: {
  el: any;
  lang?: string;
  isBilingual?: boolean;
  layout: 'side' | 'stacked';
  isThermal?: boolean;
}) {
  const rtl = isRTL(lang);
  const engText = el.textContent || '';
  const rawTrans = el.arabicContent || '';
  const transText = cleanAndMirrorSymbols(engText, rawTrans);
  const hasTranslation = isBilingual && transText && transText !== engText;

  // ⚡ Customer card (grey box, left bar, monospace font) ONLY belongs on Thermal receipts!
  // On A4 and Letter, NEVER force card styling — preserve the template's natural font and layout.
  const isCard = isThermal && (el.id === 'auto-injected-client-box' || isClientCardText(engText));
  const barColor = el.borderLeftColor || '#7c3aed';

  const cardBoxStyle: React.CSSProperties = isCard ? {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderLeft: `4px solid ${barColor}`,
    borderRadius: 4,
    padding: '8px 12px',
    margin: '6px 0',
    boxSizing: 'border-box',
    fontFamily: 'monospace',
  } : {};

  const engStyle: React.CSSProperties = {
    fontSize: el.fontSize || 10.5,
    fontFamily: isCard ? 'monospace' : (el.fontFamily || 'Inter, Arial, sans-serif'),
    fontWeight: el.fontWeight || 'normal',
    fontStyle: el.fontStyle || 'normal',
    color: el.color || '#000',
    textAlign: (el.textAlign as any) || 'left',
    whiteSpace: 'pre-wrap',
    lineHeight: el.lineHeight || 1.4,
    wordBreak: 'break-word',
  };

  const transStyle: React.CSSProperties = {
    fontSize: Math.round((el.fontSize || 10.5) * 0.9),
    fontFamily: isCard ? 'monospace' : getTransFont(lang),
    fontWeight: el.fontWeight || 'normal',
    color: el.color || '#000',
    textAlign: rtl ? 'right' : 'left',
    direction: rtl ? 'rtl' : 'ltr',
    whiteSpace: 'pre-wrap',
    lineHeight: el.lineHeight || 1.4,
    opacity: 0.85,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    minWidth: 0,
    maxWidth: '100%',
  };

  // No translation — single column
  if (!hasTranslation) {
    return (
      <div data-elid={el.id} style={{ ...cardBoxStyle, ...engStyle, margin: isCard ? '6px 0' : '2px 0' }}>
        {isCard ? renderClientCardLines(engText, false, true) : engText}
      </div>
    );
  }

  // Side-by-side (English left | Hindi right) with purple vertical bar
  if (layout === 'side') {
    return (
      <div
        data-elid={el.id}
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          margin: isCard ? '6px 0' : '2px 0',
          ...cardBoxStyle,
        }}
      >
        <div style={{ flex: '1 1 50%', minWidth: 0, ...engStyle }}>
          {isCard ? renderClientCardLines(engText, false, true) : engText}
        </div>
        <div style={{ flex: '1 1 50%', minWidth: 0, ...transStyle }}>
          {isCard ? renderClientCardLines(transText, rtl, false) : renderBidiSafeLines(transText, rtl, lang)}
        </div>
      </div>
    );
  }

  // Stacked layout
  return (
    <div data-elid={el.id} style={{ margin: isCard ? '6px 0' : '2px 0', ...cardBoxStyle }}>
      <div style={engStyle}>{isCard ? renderClientCardLines(engText, false, true) : engText}</div>
      <div style={{ ...transStyle, marginTop: 2 }}>
        {isCard ? renderClientCardLines(transText, rtl, false) : renderBidiSafeLines(transText, rtl, lang)}
      </div>
    </div>
  );
}

function renderClientCardLines(text: string, rtl: boolean, mono: boolean) {
  const lines = text.split('\n').filter(l => l.trim());
  return lines.map((line, i) => {
    const t = line.trim();

    // 1. Line 0 is Customer Name (Bold Dark)
    const isName = i === 0;

    // 2. Universal Contact Detection: email symbol, phone digits, or any Label: +number/email
    const isContact =
      t.includes('@') ||
      /^[\d\s+\-().]{7,}$/.test(t) ||
      /^[^:\n]{1,25}:\s*(\+?\d|.*@)/i.test(t) ||
      /^(email|phone|ph|tel|mob|address|addr)\b/i.test(t);

    // 3. Universal Tax ID Detection: checks alphanumeric codes or global tax identifiers (GSTIN, VAT, NPWP, NIF, CIF, INN, etc.)
    const isTax = !isName && !isContact && (
      /(tax|gst|vat|trn|npwp|nif|cif|inn|rfc)\b/i.test(t) ||
      /^[^:\n]{1,25}:\s*[A-Z0-9]{6,}/i.test(t) ||
      /\b[A-Z0-9]{8,}\b/.test(t)
    );

    const latinOnly = !/[^\u0000-\u007F]/.test(t);
    return (
      <div
        key={i}
        style={{
          fontFamily: mono ? 'monospace' : undefined,
          fontWeight: isName || isTax ? 700 : 400,
          color: isTax ? '#047857' : isName ? '#0f172a' : '#475569',
          fontSize: isName ? '1.05em' : undefined,
          marginBottom: isName ? 3 : 1,
          ...(latinOnly && rtl ? { direction: 'ltr', unicodeBidi: 'embed', textAlign: 'right' } : {}),
        }}
      >
        {t}
      </div>
    );
  });
}

// ─── Main renderer ────────────────────────────────────────────────────────────

export default function BilingualInvoiceRenderer({
  template, clientName, clientEmail, clientPhone, clientAddress, buyerTaxId, productTaxName, taxBreakdown: propTaxBreakdown,
  invoiceDate, invoiceTime, notes, notesTranslated = '', lineItems, taxLines, discountLines,
  autoDiscountAmount = 0, autoDiscountLabel, autoDiscountLabelAr,
  sym, shippingCost, invoiceStatus, translateTargetLang, isBilingual = false,
  isB2B = false,
  bilingualLayout = 'side', isThermal = false, isThermal58 = false,
  showAmountInWords = false, currencyCode = 'INR', amountInWordsTranslated = '', amountInWordsStyle, savedElements,
  totalsLabels,
  showTrackingQR = false,
  autoQrPosition,
  zoom = 1,
  cashierName = '',
  pharmacistName = '',
  cashierNameTranslit = '',
  pharmacistNameTranslit = '',
  onQrMouseDown,
  qrOffset = { x: 0, y: 0 },
  invoiceNumber,
  dynamicValues,
  dynamicValuesAr,
  dynamicFieldLabelsAr,
  taxScheme,
  sellerRegion,
  buyerRegion,
  taxBreakdownAr,
  qrCaptionInvoiceAr = '',
  qrCaptionTrackingAr = '',
}: BilingualInvoiceRendererProps) {

  // Auto-detect stored lang from elements or backend invoice record (defaults to English!)
  const storedLang = (template as any)?.translated_lang || 
                     (template as any)?.translatedLang || 
                     template?.elements?.find((el: any) => el.translatedLang)?.translatedLang;
  const lang = translateTargetLang || storedLang || 'en';

  // ⚡ When translation is OFF (or lang is English), DO NOT translate anything!
  const isBilingualActive = Boolean(
    (isBilingual !== undefined ? isBilingual : ((template as any)?.is_bilingual || (template as any)?.isBilingual))
    && lang !== 'en'
  );

  // Auto-resolve dynamic values, labels, and tax transliterations from saved invoice
  const effectiveDynValuesAr = dynamicValuesAr || (template as any)?.dynamic_values_ar || (template as any)?.dynamicValuesAr;
  const effectiveDynFieldLabelsAr = dynamicFieldLabelsAr || (template as any)?.dynamic_field_labels_ar || (template as any)?.dynamicFieldLabelsAr;
  const effectiveTaxBreakdownAr = taxBreakdownAr || (template as any)?.tax_breakdown_ar || (template as any)?.taxBreakdownAr || {};

  // Find saved amount-in-words element to extract saved translation and toggle state
  const savedWordsEl = template?.elements?.find((el: any) => 
    el.id === 'pdf-amount-in-words' || 
    el.id === 'preview-amount-in-words' || 
    (el.type === 'text' && /^amount in words/i.test((el.textContent || '').trim()))
  );

  const hasAmountInWords = Boolean(
    showAmountInWords ||
    (template as any)?.show_amount_in_words ||
    (template as any)?.showAmountInWords ||
    savedWordsEl
  );

  const effectiveAmountInWordsTranslated = amountInWordsTranslated ||
                                           (template as any)?.amount_in_words_translated ||
                                           (template as any)?.amountInWordsAr ||
                                           (template as any)?.dynamic_values_ar?.amountInWords ||
                                           savedWordsEl?.arabicContent ||
                                           '';
  const rtl = isRTL(lang);
  const arabicFont = '"Noto Sans Arabic", Arial, sans-serif';
  const [tl, setTl] = useState(DEFAULT_TOTAL_LABELS);
  const [taxLabelTranslations, setTaxLabelTranslations] = useState<Record<string, string>>({});
  const [qrLabelTranslations, setQrLabelTranslations] = useState<Record<string, string>>({});
  const [autoQrLabels, setAutoQrLabels] = useState<{ invoice: string; tracking: string }>({ invoice: '', tracking: '' });

  // Auto-translate any template QR labels (like "Scan to Pay Net30") for all 100+ languages
  useEffect(() => {
     if (!isBilingualActive || !lang || lang === 'en') { setQrLabelTranslations({}); return; }
    const allEls = [...(template?.elements || []), ...(savedElements || [])];
    const labelsToTranslate = allEls
      .filter((e: any) => (e.type === 'qr' && e.label && !e.arabicLabel && !e.labelAr) || (e.type === 'text' && /^scan to/i.test((e.textContent || '').trim()) && !e.arabicContent))
      .map((e: any) => (e.label || e.textContent).trim())
      .filter(Boolean);

    const uniqueLabels = Array.from(new Set(labelsToTranslate));
    if (uniqueLabels.length === 0) return;

    let cancelled = false;
    (async () => {
      const results = await translateBatchViaBackend(uniqueLabels, lang);
      if (!cancelled && results && results.length) {
        const map: Record<string, string> = {};
        uniqueLabels.forEach((lbl, idx) => {
          if (results[idx] && results[idx] !== lbl) {
            map[lbl] = results[idx];
          }
        });
        setQrLabelTranslations(prev => ({ ...prev, ...map }));
      }
    })();
    return () => { cancelled = true; };
  }, [lang, template, savedElements]);
  const [statusStampTranslation, setStatusStampTranslation] = useState<string>('');
  const [customStampTranslations, setCustomStampTranslations] = useState<Record<string, string>>({});

  const activeTl = {
    subtotal: totalsLabels?.subtotal || tl.subtotal,
    total: totalsLabels?.total || tl.total,
    tax: totalsLabels?.tax || tl.tax,
    discount: totalsLabels?.discount || tl.discount,
    shipping: totalsLabels?.shipping || tl.shipping,
  };

  // 🌐 100% Dynamic Backend Translation for Totals (Zero hardcoded dictionaries)
  useEffect(() => {
    if (!isBilingualActive || !lang || lang === 'en') {
      setTl(totalsLabels || DEFAULT_TOTAL_LABELS);
      return;
    }
    if (totalsLabels) {
      setTl(totalsLabels);
      return;
    }

    let cancelled = false;
    translateTotalLabels(lang).then(translatedObj => {
      if (!cancelled && translatedObj) {
        setTl(translatedObj);
      }
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [lang, isBilingualActive, totalsLabels]);


// Dynamically translate Status Banner and all Stamps for all 100+ languages
useEffect(() => {
  if (!isBilingualActive || !lang || lang === 'en') {
    setStatusStampTranslation('');
    setCustomStampTranslations({});
    return;
  }
  let cancelled = false;
  (async () => {
    // 1. Status stamp translation (e.g. PAID IN FULL, PAID, OVERDUE, CANCELLED)
    if (invoiceStatus) {
      const statusText = ['paid', 'Paid'].includes(invoiceStatus) ? 'Paid in Full' : invoiceStatus;
      const res = await translateViaBackend(statusText, lang);
      if (!cancelled && res && res.toLowerCase() !== statusText.toLowerCase()) {
        setStatusStampTranslation(res);
      }
    }

    // 2. Any template stamp elements
    const stampElements = (template?.elements || []).filter((el: any) => el.type === 'stamp' && el.stampText?.trim());
    if (stampElements.length > 0) {
      const stampPairs = await Promise.all(
        stampElements.map(async (el: any) => {
          const text = el.stampText.trim();
          const tr = await translateViaBackend(text, lang);
          return [text, tr] as const;
        })
      );
      if (!cancelled) {
        setCustomStampTranslations(Object.fromEntries(stampPairs));
      }
    }
  })();
  return () => { cancelled = true; };
}, [lang, invoiceStatus, template]);

useEffect(() => {
  if (!isBilingualActive || !lang || lang === 'en') { setAutoQrLabels({ invoice: '', tracking: '' }); return; }
  // Only self-translate whichever caption the caller didn't already
  // supply. Requiring BOTH invoice AND tracking before skipping meant any
  // caller with no tracking QR (Quick Invoice never has one) always ran
  // this internal fetch anyway.
  if (qrCaptionInvoiceAr && qrCaptionTrackingAr) { setAutoQrLabels({ invoice: '', tracking: '' }); return; }
  let cancelled = false;
  (async () => {
    const [invoice, tracking] = await Promise.all([
      qrCaptionInvoiceAr ? Promise.resolve('') : translateViaBackend('Scan to view invoice', lang),
      qrCaptionTrackingAr ? Promise.resolve('') : translateViaBackend('Scan to track your order', lang),
    ]);
    if (!cancelled) setAutoQrLabels({ invoice, tracking });
  })();
  return () => { cancelled = true; };
}, [lang, qrCaptionInvoiceAr, qrCaptionTrackingAr]);

useEffect(() => {
  if (!isBilingualActive || !lang || lang === 'en') { setTaxLabelTranslations({}); return; }
  let cancelled = false;

  // 100% Dynamic Label Translations (Zero hardcoded dictionaries)
  const taxLabelsFromLines = taxLines.filter(t => !(t as any).translatedLabel).map(t => t.label);
  const discountLabelsFromLines = discountLines.filter(d => !(d as any).translatedLabel).map(d => d.label);
  const customTaxNamesFromItems = lineItems.map(li => li.taxName).filter(Boolean) as string[];
  const customTaxNamesFromTemplate = (((template as any)?.elements?.find((e: any) => e.type === 'table') as any)?.taxBreakdown || (template as any)?.tax_breakdown || []).map((e: any) => e.name);
  const labels = Array.from(new Set([
    ...taxLabelsFromLines,
    ...discountLabelsFromLines,
    ...customTaxNamesFromItems,
    ...customTaxNamesFromTemplate,
    autoDiscountLabel || 'Loyalty Discount'
  ].filter(Boolean)));

  if (labels.length === 0) return;
  (async () => {
    // Translates all labels in 1 single batched API call for whichever language is active
    const rawResults = await translateBatchViaBackend(labels, lang);
    const results = rawResults.map((translated, i) =>
      translated && translated !== labels[i] ? translated : null
    );
    if (!cancelled) {
      const map: Record<string, string> = {};
      labels.forEach((label, i) => {
        if (results[i]) map[label] = results[i] as string;
      });
      if (Object.keys(map).length > 0) {
        setTaxLabelTranslations(prev => ({ ...prev, ...map }));
      }
    }
  })();
  return () => { cancelled = true; };
}, [lang, isBilingualActive, taxLines.map(t => t.label).join(','), discountLines.map(d => d.label).join(','), autoDiscountLabel]);


  const isAr = lang === 'ar';
  const isBilingualEffective = isBilingualActive;
  const layout = bilingualLayout;

  // If not bilingual → render nothing (caller falls back to TemplatePreviewCanvas)
  // This component should only be mounted when isBilingual=true
  const activeItems = lineItems.filter(li => (li.description || (li as any).values?.description || (li as any).name)?.trim());

  // Helper to determine whether B2B or B2C rate applies
  const getEffectiveRate = (li: LineItem): number => {
    const rawB2b = (li as any).b2bTaxRate ?? (li as any).b2b_tax_rate;
    if (isB2B && rawB2b !== undefined && rawB2b !== null) {
      return parseFloat(String(rawB2b)) || 0;
    }
    return parseFloat(String(li.taxRate ?? (li as any).tax_rate ?? 0)) || 0;
  };

  // Per-item tax breakdown — mirrors POSView/QuickInvoiceView's
  // getItemTaxBreakdown exactly. `base` is always the pre-tax (net) amount
  // whether the item's tax is inclusive or exclusive, so manual Tax/Discount
  // lines (a % of subtotal) never compound on top of tax already baked
  // into an inclusive price.
  const itemTaxBreakdowns = activeItems.map(li => {
    const lineTotal = li.qty * li.rate;
    const rate = getEffectiveRate(li);
    if (rate <= 0) return { base: lineTotal, tax: 0 };
    if (li.taxInclusive) {
      const base = lineTotal / (1 + rate / 100);
      return { base, tax: lineTotal - base };
    }
    return { base: lineTotal, tax: lineTotal * (rate / 100) };
  });
  const subtotal = itemTaxBreakdowns.reduce((s, b) => s + b.base, 0);
  const discTotal = discountLines.reduce((s, d) => s + subtotal * (d.rate / 100), 0);

  // Discount reduces the TAXABLE VALUE before tax is calculated — same
  // standard GST/VAT math as every other totals block in the app, kept
  // in sync here so bilingual invoices match the English-only total.
  const taxReducingDiscount = discTotal + autoDiscountAmount;
  const discountRatio = subtotal > 0 ? Math.min(1, taxReducingDiscount / subtotal) : 0;
  const productTaxTotal = itemTaxBreakdowns.reduce((s, b, i) => {
    const rate = getEffectiveRate(activeItems[i]);
    return s + (b.base * (1 - discountRatio)) * (rate / 100);
  }, 0);
  const taxableValue = subtotal - taxReducingDiscount;
  const taxTotal = taxLines.reduce((s, t) => s + taxableValue * (t.rate / 100), 0);
  const grandTotal = taxableValue + productTaxTotal + taxTotal + shippingCost;

  const { headerEls, tableEl, footerEls } = extractSections(template, savedElements);

  // Named per-component or custom B2B/B2C tax breakdown
  let taxBreakdownEntries: { name: string; rate: number; amount: number }[] | null = null;
  if (propTaxBreakdown && propTaxBreakdown.length > 0) {
    taxBreakdownEntries = propTaxBreakdown;
  } else if ((tableEl as any)?.taxBreakdown && (tableEl as any).taxBreakdown.length > 0) {
    // Read directly from the saved table element (e.g. "rihan", "VAT", etc.)
    taxBreakdownEntries = (tableEl as any).taxBreakdown;
  } else if ((template as any)?.tax_breakdown && (template as any).tax_breakdown.length > 0) {
    taxBreakdownEntries = (template as any).tax_breakdown;
  } else if (productTaxTotal > 0) {
    const combined: Record<string, { rate: number; amount: number }> = {};
    const isCustomScheme = taxScheme?.id === 'outlet_custom' || taxScheme?.id === 'custom';
    const b2bComps = isCustomScheme ? ((taxScheme as any)?.b2b_components) : null;
    const hasB2BSplit = isB2B && b2bComps && b2bComps.length > 1;

    activeItems.forEach((li, i) => {
      const rate = getEffectiveRate(li);
      if (rate <= 0) return;
      const discountedBase = itemTaxBreakdowns[i].base * (1 - discountRatio);

      if (hasB2BSplit) {
        b2bComps.forEach((c: any) => {
          if (!combined[c.name]) combined[c.name] = { rate: c.rate, amount: 0 };
          combined[c.name].amount += (discountedBase * c.rate / 100);
        });
      } else if (!isB2B && taxScheme?.kind === 'component' && taxScheme?.components && taxScheme?.components.length > 1) {
        const breakdown = computeTaxBreakdown(
          taxScheme, discountedBase,
          { sellerRegion, buyerRegion }, rate
        );
        breakdown.lines.forEach(l => {
          if (!combined[l.name]) combined[l.name] = { rate: l.rate, amount: 0 };
          combined[l.name].amount += l.amount;
        });
      } else {
        // Flat / single custom tax (e.g. rihan / rihan4)
        const b2bFallbackName = (taxScheme as any)?.b2b_name || (li.taxName ? `${li.taxName} B2B` : 'B2B Tax');
        const customLabel = isB2B
          ? (li.b2bTaxName || b2bFallbackName)
          : (li.taxName || taxScheme?.components?.[0]?.name || taxScheme?.label?.replace(/\s*\(.*?\)/, '') || productTaxName || 'Tax');

        if (!combined[customLabel]) combined[customLabel] = { rate, amount: 0 };
        combined[customLabel].amount += (discountedBase * rate / 100);
      }
    });

    const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
    if (entries.length > 0) taxBreakdownEntries = entries;
  }

  const columns = tableEl?.columns || [
    { key: 'description', header: 'Item', width: 50, align: 'left' },
    { key: 'qty', header: 'Qty', width: 15, align: 'center' },
    { key: 'rate', header: 'Rate', width: 17, align: 'right' },
    { key: 'total', header: 'Amt', width: 18, align: 'right' },
  ];

  const headerColor = tableEl?.color || '#4f46e5';
  const tableFont = tableEl?.fontFamily || 'Inter, Arial, sans-serif';
  const tableFontSize = tableEl?.fontSize || 11;
  const containerWidth = isThermal58 ? 220 : isThermal ? 302 : 794;

  const statusColor = ['paid','Paid'].includes(invoiceStatus || '') ? '#10b981'
    : ['overdue','Overdue'].includes(invoiceStatus || '') ? '#ef4444'
    : ['cancelled','Cancelled'].includes(invoiceStatus || '') ? '#6b7280'
    : ['unpaid','Unpaid'].includes(invoiceStatus || '') ? '#f59e0b' : null;

  // ── Bilingual label helper (English + translation stacked in one cell) ──────
  const BiLabel = ({ eng, trans }: { eng: string; trans: string }) => {
  const translatedFontSize =
    String(trans || '').length > 24
      ? tableFontSize * 0.66
      : String(trans || '').length > 16
        ? tableFontSize * 0.72
        : String(trans || '').length > 10
          ? tableFontSize * 0.76
          : tableFontSize * 0.8;

  const wrapperStyle: React.CSSProperties = {
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  };

  const safeTextStyle: React.CSSProperties = {
    minWidth: 0,
    maxWidth: '100%',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.5,
    paddingTop: 1,
  };

  // ⚡ Universal for all 100+ languages (Zero hardcoded words)
  const isDuplicateOrEmpty =
    !isBilingualActive ||
    !trans ||
    trans.trim().toLowerCase() === eng.trim().toLowerCase();

  if (isDuplicateOrEmpty) {
    return (
      <div style={wrapperStyle}>
        <span style={safeTextStyle}>{eng}</span>
      </div>
    );
  }

  if (layout === 'side') {
    return (
      <div
        style={{
          ...wrapperStyle,
          display: 'flex',
          flexWrap: 'wrap',
          columnGap: 5,
          rowGap: 2,
          alignItems: 'baseline',
        }}
      >
        <span
          style={{
            ...safeTextStyle,
            flex: '0 1 auto',
          }}
        >
          {eng}
        </span>

        <span
          style={{
            ...safeTextStyle,
            flex: '1 1 auto',
            fontSize: translatedFontSize,
            fontFamily: getTransFont(lang),
            direction: rtl ? 'rtl' : 'ltr',
            textAlign: rtl ? 'right' : 'left',
            opacity: 0.7,
          }}
        >
          {trans}
        </span>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div style={safeTextStyle}>{eng}</div>

      <div
        style={{
          ...safeTextStyle,
          fontSize: translatedFontSize,
          fontFamily: getTransFont(lang),
          direction: rtl ? 'rtl' : 'ltr',
          textAlign: rtl ? 'right' : 'left',
          opacity: 0.7,
          marginTop: 2,
        }}
      >
        {trans}
      </div>
    </div>
  );
};

  // ── Bilingual number helper (Arabic numerals only for ar) ─────────────────
  const BiNum = ({ eng }: { eng: string }) => (
  <div
    style={{
      flex: '0 0 auto',
      flexShrink: 0,
      minWidth: 'max-content',
      textAlign: 'right',
      whiteSpace: 'nowrap',
      marginLeft: 6,
    }}
  >
    <div
      style={{
        fontFamily: 'monospace',
        direction: 'ltr',
        unicodeBidi: 'embed',
        whiteSpace: 'nowrap',
      }}
    >
      {eng}
    </div>

    {isBilingualActive && isAr && (
      <div
        style={{
          fontSize: tableFontSize * 0.82,
          fontFamily: arabicFont,
          // Arabic-Indic digits are still read left-to-right as a number.
          // direction:'rtl' let the bidi algorithm reorder this mixed
          // currency-code + digit string (e.g. "-TT$١٤.٥٠" → "٠٥.٤١$TT-").
          // Force LTR embedding, same protection already used for the
          // English line right above.
          direction: 'ltr',
          unicodeBidi: 'embed',
          color: '#64748b',
          marginTop: 1,
          whiteSpace: 'nowrap',
        }}
      >
        {arNum(eng)}
      </div>
    )}
  </div>
);

  // ── Render a non-table, non-image element ─────────────────────────────────
  const renderEl = (el: any, captionEl?: any) => {
    if (el.type === 'image' && el.textContent) {
      return (
        <div key={el.id} style={{ textAlign: 'center', margin: '6px 0' }}>
          <img src={el.textContent} alt="" style={{ maxWidth: '100%', maxHeight: 100, objectFit: 'contain' }} />
        </div>
      );
    }
    if (el.type === 'shape') {
      const hasFill = el.backgroundColor && el.backgroundColor !== 'transparent';
      if (hasFill) {
        return (
          <div
            key={el.id}
            style={{
              width: '100%',
              height: el.height || 4,
              backgroundColor: el.backgroundColor,
              margin: '0 0 6px 0',
            }}
          />
        );
      }
      const h = Math.max(1, el.borderWidth || 1);
      return <div key={el.id} style={{ borderTop: `${h}px solid ${el.borderColor || '#e2e8f0'}`, margin: '6px 0' }} />;
    }
if (el.type === 'qr') {
  // Balanced standard QR size (100px on A4, 80px on thermal)
  const defaultSize = isThermal58 ? 65 : isThermal ? 80 : 100;
  const qrSize = Math.min(Math.max(el.width || defaultSize, 60), isThermal ? 85 : 105);
  const qrStyle: React.CSSProperties = {
    width: `${qrSize}px`,
    height: `${qrSize}px`,
    maxWidth: `${qrSize}px`,
    maxHeight: `${qrSize}px`,
    objectFit: 'contain',
    display: 'block',
  };
  const qrSvg = el.qrValue ? (
    <img
      src={`https://api.qrserver.com/v1/create-qr-code/?size=${qrSize * 2}x${qrSize * 2}&data=${encodeURIComponent(el.qrValue)}`}
      width={qrSize}
      height={qrSize}
      style={qrStyle}
      alt="QR code"
    />
  ) : el.imageDataUrl ? (
    <img
      src={el.imageDataUrl}
      width={qrSize}
      height={qrSize}
      style={qrStyle}
      alt="QR code"
    />
  ) : (
    <div style={{ width: qrSize, height: qrSize, background: '#f1f5f9', border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#94a3b8', borderRadius: 4 }}>
      QR after save
    </div>
  );

  const elCenterX = (el.x || 0) + (el.width || qrSize) / 2;
  const relPos = containerWidth > 0 ? elCenterX / containerWidth : 0.5;
  const flexAlign = relPos < 0.33 ? 'flex-start' : relPos > 0.66 ? 'flex-end' : 'center';
  const textAlignForCaption = flexAlign === 'flex-start' ? 'left' : flexAlign === 'flex-end' ? 'right' : 'center';

  // Support bundled label properties
  const showBundledLabel = el.showLabel !== false && Boolean(el.label);
  const isTop = el.labelPosition === 'top';
  const labelText = el.label || '';
  const labelTextAr = el.arabicLabel || el.labelAr || qrLabelTranslations[el.label?.trim()] || '';

  const captionNode = (
    <>
      {showBundledLabel && (
        <div style={{ marginTop: isTop ? 0 : 4, marginBottom: isTop ? 4 : 0, textAlign: textAlignForCaption }}>
          <div style={{
            fontSize: 10,
            fontFamily: 'Inter, Arial, sans-serif',
            color: '#000000',
            fontWeight: 800,
            maxWidth: qrSize + 20,
            wordBreak: 'break-word',
          }}>
            {labelText}
          </div>
          {isBilingual && labelTextAr && (
            <div style={{
              fontSize: 9,
              fontFamily: getTransFont(lang),
              direction: rtl ? 'rtl' : 'ltr',
              color: '#000000',
              fontWeight: 700,
              marginTop: 2,
              maxWidth: qrSize + 20,
              wordBreak: 'break-word',
            }}>
              {labelTextAr}
            </div>
          )}
        </div>
      )}

      {!showBundledLabel && captionEl && (
        <div style={{ marginTop: 4, textAlign: textAlignForCaption }}>
          <div style={{
            fontSize: captionEl.fontSize || 10,
            fontFamily: captionEl.fontFamily || 'Inter, Arial, sans-serif',
            color: '#000000',
            fontWeight: 800,
          }}>
            {captionEl.textContent}
          </div>
          {isBilingualActive && (captionEl.arabicContent || qrLabelTranslations[(captionEl.textContent || '').trim()]) && (
            <div style={{
              fontSize: (captionEl.fontSize || 10) * 0.9,
              fontFamily: getTransFont(lang),
              direction: rtl ? 'rtl' : 'ltr',
              color: '#000000',
              fontWeight: 700,
              marginTop: 2,
            }}>
              {captionEl.arabicContent || qrLabelTranslations[(captionEl.textContent || '').trim()]}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div key={el.id} style={{ display: 'flex', flexDirection: isTop ? 'column-reverse' : 'column', alignItems: flexAlign, margin: '6px 0' }}>
      {qrSvg}
      {captionNode}
    </div>
  );
}
    if (el.type === 'stamp' || el.type === 'signature') {
      // Page has 36px side padding on A4; keep the element at its saved x.
      const leftPad = isThermal ? 0 : Math.max(0, (el.x || 0) - 36);
      if (el.type === 'stamp') {
        if (!el.stampText) return null;
        const color = el.color || '#0ea5e9';
        const stampTrans = el.arabicContent || customStampTranslations[el.stampText.trim()] || '';

        return (
          <div key={el.id} style={{ paddingLeft: leftPad, margin: '6px 0' }}>
            <div style={{
              display: 'inline-block',
              border: `3px solid ${color}`,
              borderRadius: el.stampShape === 'badge' ? 999 : 6,
              padding: '8px 18px',
              transform: `rotate(${el.rotation || 0}deg)`,
              opacity: (el.opacity ?? 100) / 100,
              color,
              fontWeight: 800,
              fontSize: 16,
              fontFamily: 'Inter, Arial, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: 1,
              whiteSpace: 'nowrap',
            }}>
              <div style={{ textAlign: 'center' }}>{el.stampText}</div>
              {isBilingualActive && stampTrans && stampTrans !== el.stampText && (
                <div style={{
                  textAlign: 'center',
                  fontFamily: getTransFont(lang),
                  direction: rtl ? 'rtl' : 'ltr',
                  textTransform: 'none',
                  letterSpacing: 0,
                  fontSize: 13,
                  marginTop: 2,
                  fontWeight: 600,
                }}>
                  {stampTrans}
                </div>
              )}
            </div>
          </div>
        );
      }
      const staff = cashierName?.trim() || 'Staff';
      const staffAr = cashierNameTranslit?.trim() || staff;
      const sigName = (el.signatureName || staff).replace(/\{staff_name\}/gi, staff);
      const sigNameAr = (el.arabicSignatureName || staffAr).replace(/\{staff_name\}/gi, staffAr);

      return (
        <div key={el.id} style={{ paddingLeft: leftPad, margin: '6px 0' }}>
          <div style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
            <div style={{
              fontFamily: `${el.fontFamily ? `'${el.fontFamily}'` : "'Alex Brush'"},'Dancing Script','Brush Script MT',cursive`,
              fontSize: el.fontSize || 24,
              fontWeight: 700,
              color: el.color || '#1e293b',
              borderBottom: '1px solid #94a3b8',
              paddingBottom: 3,
              marginBottom: 3,
            }}>{sigName}</div>
            {isBilingual && sigNameAr && sigNameAr !== sigName && (
              <div style={{ fontSize: Math.round((el.fontSize || 24) * 0.7), fontStyle: 'italic', color: el.color || '#1e293b', fontFamily: `${el.fontFamily ? `'${el.fontFamily}'` : "'Alex Brush'"}, ${getTransFont(lang)}`, direction: rtl ? 'rtl' : 'ltr', textAlign: 'left', marginBottom: 3 }}>{sigNameAr}</div>
            )}
            <div style={{ fontSize: 8.5, color: '#64748b', letterSpacing: 0.5 }}>{el.textContent}</div>
            {isBilingual && el.arabicContent && el.arabicContent !== el.textContent && (
              <div style={{ fontSize: 8, color: '#64748b', fontFamily: getTransFont(lang), direction: rtl ? 'rtl' : 'ltr', textAlign: 'left' }}>{el.arabicContent}</div>
            )}
          </div>
        </div>
      );
    }
    if (el.type === 'text') {
  const patchedEng = patchElText(el, {
    clientName, clientEmail, clientPhone, clientAddress, buyerTaxId,
    invoiceDate, invoiceTime, notes,
    cashierName, pharmacistName,
    dynamicValues,
    invoiceNumber,
  });
 const patchedArabic = patchElArabicText(
  el,
  patchedEng,
  dynamicValues,
  dynamicValuesAr,
  dynamicFieldLabelsAr,
  lang,
  invoiceNumber,
  cashierName,
  cashierNameTranslit,
  pharmacistName,
  pharmacistNameTranslit
);
  return (
    <TextBlock
      key={el.id}
      el={{ ...el, textContent: patchedEng, arabicContent: patchedArabic }}
      lang={lang}
      isBilingual={isBilingualActive}
      layout={/^scan to/i.test(patchedEng.trim()) ? 'stacked' : layout}
      isThermal={isThermal || isThermal58}
    />
  );
}
    return null;
  };

  return (
    <div style={{
      width: containerWidth,
      zoom: zoom,
      transition: 'zoom 0.1s ease-out',
    }}>
    <div data-bilingual-invoice-page="true" style={{
      width: containerWidth,
      background: '#fff',
      fontFamily: 'Inter, Arial, sans-serif',
      fontSize: 11,
      color: '#111',
      padding: isThermal58 ? '6px 6px' : isThermal ? '12px 10px' : '28px 36px',
      boxSizing: 'border-box',
    }}>

      {/* ── HEADER ELEMENTS ── */}
      <div style={{ marginBottom: 14 }}>
        {(() => {
          const { groups, remaining } = groupOverlappingShapes(headerEls);
          // Builds the raw patched English/Arabic text pair for one
          // element — same patching renderEl's own 'text' branch does,
          // extracted here so renderOverlapGroup can lay the two
          // languages out as fixed columns instead of per-element pairs.
          const getPatchedPair = (el: any) => {
            const patchedEng = patchElText(el, {
              clientName, clientEmail, clientPhone, clientAddress, buyerTaxId,
              invoiceDate, invoiceTime, notes,
              cashierName, pharmacistName,
              dynamicValues,
              invoiceNumber,
            });
            const patchedArabic = patchElArabicText(
              el,
              patchedEng,
              dynamicValues,
              dynamicValuesAr,
              dynamicFieldLabelsAr,
              lang,
              invoiceNumber,
              cashierName,
              cashierNameTranslit,
              pharmacistName,
              pharmacistNameTranslit
            );
            return { textContent: patchedEng, arabicContent: patchedArabic };
          };
          const units = [
            ...groups.map(g => ({ y: g.shape.y || 0, render: () => renderOverlapGroup(g.shape, g.children, getPatchedPair, isBilingual, lang) })),
            ...remaining.map(el => ({ y: el.y || 0, render: () => renderEl(el) })),
          ].sort((a, b) => a.y - b.y);
          return units.map((u, i) => <React.Fragment key={i}>{u.render()}</React.Fragment>);
        })()}
      </div>

      {/* ── ITEMS TABLE ── */}
      <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontFamily: tableFont, fontSize: tableFontSize, marginBottom: 8 }}>
        {/* col.width (a percentage) was computed correctly upstream
            (buildCwElements / the customColumns-rebalance effect) but
            never actually applied to the table — with no <colgroup> and
            no table-layout:fixed, the browser auto-sizes each column by
            content instead, squeezing narrow custom columns down until
            their headers wrap into a tall stack. A <colgroup> + fixed
            layout makes the browser honor the percentages for real. */}
        <colgroup>
          {columns.map((col: any) => (
            <col key={col.id || col.key} style={{ width: `${col.width || (100 / columns.length)}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((col: any) => {
              const origHeader = col.originalHeader || col.header;

const transHeader =
  col.translatedHeader ||
  col.arabicHeader ||
  '';
              return (
                <th key={col.id || col.key} style={{
  background: headerColor,
  color: headerColor === '#ffffff' ? '#1e293b' : '#fff',
  padding: '6px 5px',
  textAlign: col.align as any || 'left',
  fontWeight: 700,
  fontSize: tableFontSize,

  // Keep long bilingual headers inside their column.
  minWidth: 0,
  maxWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  verticalAlign: 'top',
}}>
  <div style={{
    minWidth: 0,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.15,
  }}>
    {origHeader}
  </div>

  {isBilingual && transHeader && (
    <div style={{
      fontSize:
        String(transHeader).length > 18
          ? tableFontSize * 0.68
          : String(transHeader).length > 10
            ? tableFontSize * 0.74
            : tableFontSize * 0.8,

      fontFamily: getTransFont(lang),
      direction: rtl ? 'rtl' : 'ltr',
      // Align to the COLUMN's own alignment, not forced right just
      // because the language is RTL — a left-aligned "Item Description"
      // column should have its Arabic sub-line sit directly under the
      // English text on the left, not shoved to the right edge of the cell.
      textAlign: (col.align as any) || 'left',

      opacity: 0.85,
      marginTop: 2,

      minWidth: 0,
      maxWidth: '100%',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      lineHeight: 1.15,
    }}>
      {transHeader}
    </div>
  )}
</th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {activeItems.map((item, rIdx) => (
            <tr key={item.id} style={{ background: rIdx % 2 === 1 ? '#f8fafc' : '#fff' }}>
              {columns.map((col: any) => {
                const k = (col.key || '').trim();
                const h = (col.header || col.originalHeader || '').trim();
                const kLower = k.toLowerCase();
                const hLower = h.toLowerCase();
                const rowVals = (item as any).values || {};

                // ⚡ 100% Generic Dynamic Resolver (CanvasWorkspace Architecture)
                let engVal = '';

                // A. Built-in Core Columns (formatting only)
                if (k === 'sno' || k === 'serial' || k === 'index' || ['#', 's.no', 'sr.', 'sr'].includes(hLower)) {
                  engVal = String(rIdx + 1);
                } else if (k === 'description' || kLower === 'item' || hLower === 'item' || hLower === 'description') {
                  engVal = item.description || rowVals.description || (item as any).name || '';
                } else if (k === 'qty' || kLower === 'quantity' || hLower === 'qty' || hLower === 'quantity') {
                  engVal = String(item.qty ?? rowVals.qty ?? '');
                } else if (k === 'rate' || kLower === 'price' || hLower === 'rate' || hLower === 'price') {
                  const rateVal = item.rate ?? rowVals.rate ?? (item as any).price;
                  engVal = rateVal !== undefined && rateVal !== null && rateVal !== ''
                    ? `${sym}${Number(rateVal).toFixed(2)}`
                    : '';
                } else if (k === 'total' || kLower === 'amount' || kLower === 'amt' || hLower === 'total' || hLower === 'amt' || hLower === 'amount') {
                  const totVal = (item.qty !== undefined && item.rate !== undefined)
                    ? (item.qty * item.rate)
                    : (rowVals.total ?? (item as any).amount ?? 0);
                  engVal = `${sym}${Number(totVal).toFixed(2)}`;
                } else {
                  // B. ANY Column User Adds (Reads whatever key/header is on row.values, item, or custom_fields)
                  const raw =
                    rowVals[k] ??
                    rowVals[h] ??
                    rowVals[kLower] ??
                    rowVals[hLower] ??
                    (item as any)[k] ??
                    (item as any)[h] ??
                    (item as any)[kLower] ??
                    (item as any)[hLower] ??
                    (item as any).custom_fields?.[k] ??
                    (item as any).custom_fields?.[h] ??
                    '';

                  // Automatic % formatting if the column is a tax rate number
                  if ((raw === '' || raw === undefined) && (kLower.includes('tax') || hLower.includes('tax'))) {
                    const r = item.taxRate ?? (item as any).tax_rate ?? rowVals.taxRate ?? rowVals.tax_rate;
                    engVal = (r !== undefined && r !== null && Number(r) > 0) ? `${Number(r)}%` : '';
                  } else {
                    engVal = typeof raw === 'object' ? JSON.stringify(raw) : String(raw ?? '');
                  }
                }

                const isItemCol = k === 'description' || kLower === 'item' || hLower === 'item' || hLower === 'description';

                // Arabic numerals conversion
                const transNumVal = isBilingualActive && isAr && !isItemCol
                  ? arNum(String(engVal)) : null;

                // Transliterated / Translated custom column text
                const customArMap = (item as any).__customAr || rowVals.__customAr || {};
                const customTransText = customArMap[k] || customArMap[h] || customArMap[kLower] || customArMap[hLower];

                // Translated item description lookup
                const transItemName = item.arabicName || (item as any).translatedDescription || (item as any).translated_description || rowVals.arabicName || rowVals.translatedDescription;

                return (
                  <td key={col.id || col.key} style={{
                    padding: '5px 8px',
                    textAlign: col.align as any || 'left',
                    fontSize: tableFontSize,
                    color: '#374151',
                    borderBottom: '1px solid #f1f5f9',
                    verticalAlign: 'top',
                    wordBreak: 'break-word',
                  }}>
                    {/* English value — always LTR */}
                    <div style={{ fontFamily: !isItemCol ? 'monospace' : 'inherit', direction: 'ltr', unicodeBidi: 'embed' }}>
                      {engVal}
                    </div>

                    {/* Translated custom-column value */}
                    {isBilingualActive && !isItemCol && !['qty','rate','total','sno','serial','index'].includes(kLower) && customTransText && (
                      <div style={{
                        fontSize: tableFontSize * 0.82,
                        fontFamily: getTransFont(lang),
                        direction: rtl ? 'rtl' : 'ltr',
                        textAlign: (col.align as any) || 'left',
                        color: '#64748b',
                        marginTop: 2,
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.2,
                        boxSizing: 'border-box',
                      }}>
                        {customTransText}
                      </div>
                    )}

                    {/* Translated item name (Universal for all 100+ languages) */}
                    {isBilingualActive && isItemCol && transItemName && (
  <div style={{
    fontSize:
      String(transItemName).length > 30
        ? tableFontSize * 0.72
        : String(transItemName).length > 18
          ? tableFontSize * 0.78
          : tableFontSize * 0.85,
    fontFamily: getTransFont(lang),
    direction: rtl ? 'rtl' : 'ltr',
    // Align with the column's alignment (left-aligned descriptions sit under English)
    textAlign: (col.align as any) || 'left',
    color: '#64748b',
    marginTop: 2,

    // Prevent long translations from leaving the table cell
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.2,
    boxSizing: 'border-box',
  }}>
    {transItemName}
  </div>
)}
                    {/* Arabic numerals for numeric cols (ar only) */}
                    {transNumVal && transNumVal !== engVal && (
                      <div style={{
                        fontSize: tableFontSize * 0.82,
                        fontFamily: arabicFont,
                        // Same LTR-embedding fix as BiNum — Arabic-Indic
                        // digits must not be reordered by an RTL container.
                        direction: 'ltr',
                        unicodeBidi: 'embed',
                        textAlign: (col.align as any) || 'left',
                        color: '#64748b',
                        marginTop: 2,
                      }}>{transNumVal}</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          {activeItems.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ padding: '12px 8px', color: '#9ca3af', textAlign: 'center', fontSize: 11 }}>
                No items
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ── TOTALS — float right box (A4 style) ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <div style={{
  width: isThermal ? '100%' : isBilingual ? '55%' : '42%',
  maxWidth: '100%',
  minWidth: 0,
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  padding: '8px 12px',
  fontSize: tableFontSize,
  background: '#fff',
  boxSizing: 'border-box',
  overflow: 'hidden',
}}>

          {/* Subtotal */}
          <div style={{
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 8,
  marginBottom: 5,
  minWidth: 0,
}}>
            <BiLabel eng="Subtotal" trans={activeTl.subtotal} />
            <BiNum eng={`${sym}${subtotal.toFixed(2)}`} />
          </div>

          {/* 1. Manual Discount lines */}
          {discountLines.filter((d: any) => (d.rate > 0 || d.amount > 0)).map((d: any, i: number) => {
            const rawLabel = String(d.label || 'Discount').trim();
            const rateVal = d.rate || (subtotal > 0 && d.amount ? parseFloat(((d.amount / subtotal) * 100).toFixed(2)) : 0);
            const rateSuffix = rateVal > 0 ? ` (-${rateVal}%)` : '';

            // Clean base name without percentage
            const cleanName = rawLabel.replace(/\s*\(-?\d+%\)/g, '').trim();
            const numMatch = cleanName.match(/\d+$/);

            const savedTrans = d.translatedLabel || 
                               d.labelAr || 
                               effectiveDynValuesAr?.[`discount_${i}`] || 
                               taxLabelTranslations[cleanName] ||
                               taxLabelTranslations[rawLabel];

            // ⚡ Only show translation if there is a real foreign translation
            let finalTrans = (isBilingualActive && savedTrans && savedTrans.toLowerCase() !== cleanName.toLowerCase()) ? savedTrans : '';
            const hasAnyDigit = /[0-9\u0660-\u0669\u06F0-\u06F9]/.test(savedTrans || '');
            if (finalTrans && numMatch && !hasAnyDigit) {
              finalTrans = `${finalTrans} ${numMatch[0]}`.trim();
            }

            const engLabel = cleanName.includes('%') ? cleanName : `${cleanName}${rateSuffix}`;
            const transLabel = finalTrans ? (finalTrans.includes('%') ? finalTrans : `${finalTrans}${rateSuffix}`) : '';

            return (
              <div key={i} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 8,
                marginBottom: 5,
                color: '#ef4444',
                minWidth: 0,
              }}>
                <BiLabel
                  eng={engLabel}
                  trans={transLabel}
                />
                <BiNum eng={`-${sym}${(d.amount ? d.amount : (subtotal * rateVal / 100)).toFixed(2)}`} />
              </div>
            );
          })}

          {/* 2. Auto / Loyalty discount */}
          {autoDiscountAmount > 0 && (() => {
            const rawLabel = (autoDiscountLabel || 'Loyalty Discount').trim();
            const cleanName = rawLabel.replace(/\s*\(-?\d+(\.\d+)?%\)/g, '').trim();
            const rateVal = (subtotal > 0 && autoDiscountAmount > 0)
              ? Math.round((autoDiscountAmount / subtotal) * 100)
              : 0;
            const rateSuffix = rateVal > 0 ? ` (-${rateVal}%)` : '';

            const numMatch = cleanName.match(/\d+$/);
            const savedTrans = (autoDiscountLabelAr || taxLabelTranslations[cleanName] || taxLabelTranslations[rawLabel] || '').trim();
            const cleanTrans = savedTrans.replace(/\s*\(-?\d+(\.\d+)?%\)/g, '').trim();
            
            // ⚡ Only show if there is a real foreign translation that differs from English
            let finalTrans = (isBilingualActive && cleanTrans && cleanTrans.toLowerCase() !== cleanName.toLowerCase()) ? cleanTrans : '';
            const hasAnyDigit = /[0-9\u0660-\u0669\u06F0-\u06F9]/.test(cleanTrans || '');
            if (finalTrans && numMatch && !hasAnyDigit) {
              finalTrans = `${finalTrans} ${numMatch[0]}`.trim();
            }

            const engLabel = cleanName.includes('%') ? cleanName : `${cleanName}${rateSuffix}`;
            const transLabel = finalTrans ? (finalTrans.includes('%') ? finalTrans : `${finalTrans}${rateSuffix}`) : '';

            return (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 8,
                marginBottom: 5,
                color: '#ef4444',
                minWidth: 0,
              }}>
                <BiLabel
                  eng={engLabel}
                  trans={transLabel}
                />
                <BiNum eng={`-${sym}${autoDiscountAmount.toFixed(2)}`} />
              </div>
            );
          })()}

          {/* 3. Named per-component tax breakdown or custom B2B/B2C tax */}
          {taxBreakdownEntries
            ? taxBreakdownEntries.map((l, i) => {
                const rawTaxName = (l.name || 'Tax').trim();
                const nameAr = ((l as any).nameAr ||
                               effectiveTaxBreakdownAr?.[rawTaxName] ||
                               taxBreakdownAr?.[rawTaxName] ||
                               taxLabelTranslations[rawTaxName] ||
                               (rawTaxName === 'Tax' && isBilingualActive ? tl.tax : '') || '').trim();

                const suffix = isB2B ? ' (ITC)' : '';
                const transSuffix = isB2B && nameAr ? ` (${tl.tax})` : '';
                const transVal = (isBilingualActive && nameAr && nameAr.toLowerCase() !== rawTaxName.toLowerCase())
                  ? `${nameAr} (${l.rate}%)${transSuffix}`
                  : '';

                return (
                  <div key={`taxbd-${i}`} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginBottom: 5,
                    color: isB2B ? '#16a34a' : '#7c3aed',
                    minWidth: 0,
                  }}>
                    <BiLabel eng={`${rawTaxName} (${l.rate}%)${suffix}`} trans={transVal} />
                    <BiNum eng={`+${sym}${l.amount.toFixed(2)}`} />
                  </div>
                );
              })
            : productTaxTotal > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 8,
                marginBottom: 5,
                color: isB2B ? '#16a34a' : '#7c3aed',
                minWidth: 0,
              }}>
                <BiLabel
                  eng={`${isB2B ? 'B2B Tax (ITC)' : 'Total Tax'} (${((productTaxTotal / subtotal) * 100).toFixed(subtotal ? 1 : 0)}%)`}
                  trans={isBilingualActive ? `${tl.tax} (${((productTaxTotal / subtotal) * 100).toFixed(subtotal ? 1 : 0)}%)` : ''}
                />
                <BiNum eng={`+${sym}${productTaxTotal.toFixed(2)}`} />
              </div>
            )}

          {/* 4. Additional Tax lines */}
          {taxLines.filter(t => t.rate > 0).map((t, i) => (
  <div key={i} style={{
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 8,
  marginBottom: 5,
  color: '#64748b',
  minWidth: 0,
}}>
    <BiLabel
  eng={`${t.label} (${t.rate}%)`}
  trans={`${(t as any).translatedLabel || taxLabelTranslations[t.label] || tl.tax} (${t.rate}%)`}
/>
    <BiNum eng={`+${sym}${(subtotal * t.rate / 100).toFixed(2)}`} />
  </div>
))}

          {/* Shipping */}
          {shippingCost > 0 && (
            <div style={{
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 8,
  marginBottom: 5,
  color: '#64748b',
  minWidth: 0,
}}>
              <BiLabel eng="Shipping" trans={tl.shipping} />
              <BiNum eng={`+${sym}${shippingCost.toFixed(2)}`} />
            </div>
          )}

          {/* Grand Total */}
          <div style={{
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 8,
  minWidth: 0,

  borderTop: '1px solid #e2e8f0',
  paddingTop: 6,
  marginTop: 4,

  fontWeight: 800,
  fontSize: tableFontSize + 2,
  color: headerColor !== '#1e293b' ? headerColor : '#4f46e5',
}}>
            <BiLabel eng="TOTAL" trans={activeTl.total} />
            <BiNum eng={`${sym}${grandTotal.toFixed(2)}`} />
          </div>

          {hasAmountInWords && (
            <div style={{
              marginTop: 8,
              paddingTop: 6,
              borderTop: '1px dashed #e2e8f0',
              fontSize: amountInWordsStyle?.fontSize ?? (tableFontSize - 1),
              color: amountInWordsStyle?.color ?? '#475569',
              fontStyle: amountInWordsStyle?.fontStyle ?? 'normal',
              fontWeight: amountInWordsStyle?.fontWeight ?? 'normal',
              fontFamily: amountInWordsStyle?.fontFamily ?? 'inherit',
              textAlign: amountInWordsStyle?.textAlign ?? 'center',
            }}>
              <div><strong>In Words:</strong> {amountToWords(grandTotal, currencyCode)}</div>
              {isBilingualActive && effectiveAmountInWordsTranslated && (
                <div style={{
                  fontFamily: getTransFont(lang),
                  direction: rtl ? 'rtl' : 'ltr',
                  textAlign: amountInWordsStyle?.textAlign ?? 'center',
                  marginTop: 3,
                  opacity: 0.85,
                }}>
                  {effectiveAmountInWordsTranslated}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── STATUS STAMP (Bilingual for all 100+ languages) ── */}
      {statusColor && invoiceStatus && (
        <div style={{
          textAlign: 'center', marginTop: 16,
          border: `3px double ${statusColor}`, borderRadius: 6, padding: '6px 0',
          color: statusColor, fontWeight: 900, fontSize: 14, letterSpacing: 2,
          textTransform: 'uppercase',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}>
          <div>{['paid','Paid'].includes(invoiceStatus) ? 'PAID IN FULL' : invoiceStatus.toUpperCase()}</div>
          {isBilingualActive && statusStampTranslation && (
            <div style={{
              fontFamily: getTransFont(lang),
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0,
              textTransform: 'none',
              direction: rtl ? 'rtl' : 'ltr',
              opacity: 0.9,
            }}>
              {statusStampTranslation}
            </div>
          )}
        </div>
      )}

      {/* ── NOTES ── */}
      {notes && (
        <div style={{ marginTop: 12, fontSize: 10, color: '#64748b', borderTop: '1px dashed #e2e8f0', paddingTop: 8 }}>
          <div>{notes}</div>
          {isBilingual && notesTranslated && (
            <div style={{
              fontFamily: getTransFont(lang),
              direction: rtl ? 'rtl' : 'ltr',
              textAlign: rtl ? 'right' : 'left',
              marginTop: 3,
              opacity: 0.85,
            }}>
              {notesTranslated}
            </div>
          )}
        </div>
      )}

      {(footerEls.length > 0 || (savedElements && savedElements.length > 0)) && (
  <div style={{ marginTop: 8 }}>
{(() => {
  const sorted = [...footerEls].sort((a: any, b: any) => (a.y || 0) - (b.y || 0));

  // Separate auto-injected QRs from regular footer elements. Check id
  // prefix as well as _auto — saved bills don't always carry a literal
  // _auto property on these elements, and without the id check they'd
  // leak into regularEls and render a second, unstyled, uncaptioned time
  // in addition to the properly-paired QR block rendered further below.
  const isAutoQrRelated = (el: any) =>
    !!el._auto ||
    el.id === 'pdf-amount-in-words' ||
    el.id === 'preview-amount-in-words' ||
    el.id === 'preview-invoice-qr' ||
    el.id === 'preview-invoice-qr-label' ||
    el.id?.startsWith('invoice-qr-') ||
    el.id?.startsWith('invoice-label-') ||
    el.id?.startsWith('token-qr-') ||
    el.id?.startsWith('token-label-');
  const regularEls = sorted.filter((el: any) => !isAutoQrRelated(el));
  const src = [
  ...sorted,
  ...(savedElements || []),
];

const normalizedSrc = src.map((el: any) => {
  if (el.id === 'preview-invoice-qr') {
    return {
      ...el,
      type: 'qr',
      _auto: 'invoice',
      qrValue:
        el.qrValue ||
        `${window.location.origin}/?i=preview`,
    };
  }

  if (el.id === 'preview-invoice-qr-label') {
    return {
      ...el,
      type: 'text',
      _auto: 'invoice_label',
      textContent:
        el.textContent || 'Scan to view invoice',
    };
  }

  if (el.id === 'preview-tracking-qr') {
    return {
      ...el,
      type: 'qr',
      _auto: 'tracking',
      qrValue:
        el.qrValue ||
        `${window.location.origin}/?t=preview`,
    };
  }

  if (el.id === 'preview-tracking-qr-label') {
    return {
      ...el,
      type: 'text',
      _auto: 'tracking_label',
      textContent:
        el.textContent || 'Scan to track order',
    };
  }

  // Match only genuine auto invoice elements — never tag custom template QRs (e.g. Net30/UPI) as 'invoice'
  if ((el.type === 'image' || el.type === 'qr') && (el.id === 'preview-invoice-qr' || el.id?.startsWith('invoice-qr-') || el._auto === 'invoice')) {
    return { ...el, _auto: 'invoice' };
  }
  if (el.type === 'text' && (el.id?.startsWith('invoice-label-') || el.id?.includes('invoice-label') || el._auto === 'invoice_label' || el.textContent === 'Scan to view invoice')) {
    return { ...el, _auto: 'invoice_label' };
  }
  if ((el.type === 'image' || el.type === 'qr') && (el.id?.startsWith('token-qr-') || el.id?.includes('token-qr') || el._auto === 'tracking')) {
    return { ...el, _auto: 'tracking' };
  }
  if (el.type === 'text' && (el.id?.startsWith('token-label-') || el.id?.includes('token-label') || el._auto === 'tracking_label' || el.textContent?.includes('Scan to track'))) {
    return { ...el, _auto: 'tracking_label' };
  }

  return el;
});

  // 1. Resolve Invoice QR (with fallback so it is NEVER missing)
  const publicToken = (template as any)?.publicToken || (template as any)?.public_token || (template as any)?.id;
  let invoiceQrEl = normalizedSrc.find(
    (el: any) => el._auto === 'invoice' && (el.type === 'qr' || el.type === 'image')
  );
  if (!invoiceQrEl && publicToken) {
    invoiceQrEl = {
      id: `invoice-qr-${publicToken}`,
      type: 'qr',
      _auto: 'invoice',
      qrValue: `${window.location.origin}/invoice/view/${publicToken}`,
      width: 100,
      height: 100,
    };
  }

  const invoiceLabelEl = normalizedSrc.find(
    (el: any) => el._auto === 'invoice_label'
  ) || {
    id: `invoice-label-fallback`,
    type: 'text',
    _auto: 'invoice_label',
    textContent: 'Scan to view invoice',
  };

  // 2. Resolve Tracking QR
  const trackingQrEl = showTrackingQR ? normalizedSrc.find((el: any) => el._auto === 'tracking' && (el.type === 'qr' || el.type === 'image')) : null;
  const trackingLabelEl = showTrackingQR ? normalizedSrc.find((el: any) => el._auto === 'tracking_label') : null;

  // Anything in savedElements that wasn't consumed as the special
  // invoice/tracking QR+label pair still needs to render — otherwise a
  // real invoice's own QR (or any other saved element) silently
  // disappears in bilingual mode even though it renders fine in the
  // non-bilingual CanvasWorkspace path.
  const consumedIds = new Set(
    [invoiceQrEl, trackingQrEl, invoiceLabelEl, trackingLabelEl]
      .filter(Boolean)
      .map((el: any) => el.id)
  );
  // savedElements (patchedTemplateRef.current.elements) is built from
  // enforceNoTableOverlap(translatedTemplateRef.current.elements) — the
  // FULL template, header/table/footer alike, not just the footer — plus
  // the repositioned auto QR elements. Every id that already exists
  // anywhere in `template` (header, table, or footer) must be excluded
  // here, or the entire header/table block renders a second time inside
  // this footer section. Only ids truly absent from the template (i.e.
  // the backend's real _auto QR elements) should pass through.
  const allTemplateIds = new Set(
    (template?.elements || []).map((el: any) => el.id)
  );
  const leftoverSavedEls = (savedElements || []).filter((el: any) => {
    if (consumedIds.has(el.id)) return false;
    if (allTemplateIds.has(el.id)) return false;
    // Once a REAL backend invoice/tracking QR has already been resolved
    // (invoiceQrEl/trackingQrEl with an id that isn't the preview
    // placeholder itself), the live-preview placeholder pair
    // (preview-invoice-qr / preview-invoice-qr-label) is a leftover
    // duplicate — same guard POSReceiptRenderer already applies before
    // drawing its own placeholder QR.
    const isPreviewInvoicePlaceholder = el.id === 'preview-invoice-qr' || el.id === 'preview-invoice-qr-label';
    const isPreviewTrackingPlaceholder = el.id === 'preview-tracking-qr' || el.id === 'preview-tracking-qr-label';
    if (isPreviewInvoicePlaceholder && invoiceQrEl && invoiceQrEl.id !== 'preview-invoice-qr') return false;
    if (isPreviewTrackingPlaceholder && trackingQrEl && trackingQrEl.id !== 'preview-tracking-qr') return false;
    return true;
  });
  const regularElsWithExtras = [...regularEls, ...leftoverSavedEls].sort(
    (a: any, b: any) => (a.y || 0) - (b.y || 0)
  );
  (window as any).ReactNativeWebView?.postMessage(JSON.stringify({
    type: 'QR_DEBUG',
    hasInvoiceQrEl: !!invoiceQrEl,
    invoiceQrValue: invoiceQrEl?.qrValue,
    savedElementsCount: savedElements?.length || 0,
    savedElementsRaw: savedElements,
    footerElsCount: footerEls.length,
  }));
  const makeQr = (el: any) => {
  if (!el) return null;

  // Exact matching uniform QR size: 100px for A4/Letter, 80px for thermal
  const qrSize = isThermal58 ? 65 : isThermal ? 80 : 100;
  const qrStyle: React.CSSProperties = {
    width: `${qrSize}px`,
    height: `${qrSize}px`,
    maxWidth: `${qrSize}px`,
    maxHeight: `${qrSize}px`,
    objectFit: 'contain',
    display: 'block',
  };

  if (el.imageDataUrl) {
    return (
      <img
        src={el.imageDataUrl}
        width={qrSize}
        height={qrSize}
        style={qrStyle}
        alt="QR code"
      />
    );
  }

  if (el.type === 'image' && el.textContent) {
    return (
      <img
        src={el.textContent}
        width={qrSize}
        height={qrSize}
        style={qrStyle}
        alt="QR code"
      />
    );
  }

  if (!el.qrValue) return null;

  return (
    <img
      src={`https://api.qrserver.com/v1/create-qr-code/?size=${qrSize * 2}x${qrSize * 2}&margin=1&data=${encodeURIComponent(el.qrValue)}`}
      width={qrSize}
      height={qrSize}
      style={qrStyle}
      alt="QR code"
    />
  );
};

  // When only ONE auto QR exists (the common case — just the invoice QR,
  // no tracking), derive its alignment from where it was actually placed
  // (invoiceQrEl.x, set by the backend's repositionAutoGroups) — the same
  // way every other QR in this file aligns itself. Trusting the
  // autoQrPosition PROP instead breaks for any caller that doesn't thread
  // it through (e.g. PublicInvoiceView never passes it), silently
  // falling back to 'center' regardless of what position was actually
  // chosen when the invoice was generated.
  const singleAutoQrEl = (invoiceQrEl && !trackingQrEl) ? invoiceQrEl : (!invoiceQrEl && trackingQrEl) ? trackingQrEl : null;
  // Check the prop first, then check the invoice/template's saved qr_position
  const resolvedQrPos = autoQrPosition || 
                        (template as any)?.qr_position || 
                        (template as any)?.qrPosition;

  let autoQrJustify: 'flex-start' | 'flex-end' | 'center' | 'space-between' = 'center';
  if (invoiceQrEl && trackingQrEl) {
    autoQrJustify = 'space-between';
  } else if (resolvedQrPos) {
    autoQrJustify = resolvedQrPos === 'left' ? 'flex-start' : resolvedQrPos === 'right' ? 'flex-end' : 'center';
  } else if (singleAutoQrEl && singleAutoQrEl.x !== undefined) {
    const singleQrSize = singleAutoQrEl.width || (isThermal ? 80 : 100);
    const singleQrCenterX = (singleAutoQrEl.x || 0) + singleQrSize / 2;
    const singleQrRelPos = containerWidth > 0 ? singleQrCenterX / containerWidth : 0.5;
    autoQrJustify = singleQrRelPos < 0.38 ? 'flex-start' : singleQrRelPos > 0.62 ? 'flex-end' : 'center';
  }

  return (
    <div style={{ marginTop: 8 }}>
      {/* Regular footer elements (notes, terms, signatures etc) — starts
          measuring gap from the table's original design bottom so the
          customer's spacing survives into the bilingual PDF. */}
      {renderElList(regularElsWithExtras, renderEl, (tableEl?.y || 0) + (tableEl?.height || 0))}

      {/* Auto-injected QR pair — side by side on A4/Letter, stacked
          vertically on thermal (48/58/80mm — not enough width for two
          QRs side by side). */}
{(invoiceQrEl || trackingQrEl) && (
  <div style={{
    display: 'flex',
    flexDirection: isThermal ? 'column' : 'row',
    justifyContent: isThermal ? 'flex-start' : autoQrJustify,
    alignItems: isThermal ? 'center' : 'flex-start',
    gap: isThermal ? 12 : 0,
    marginTop: 12,
    borderTop: '1px dashed #e2e8f0',
    paddingTop: 8,
  }}>
    {/* Invoice QR */}
    {invoiceQrEl && (
      <div
        data-elid="preview-invoice-qr"
        onMouseDown={(e) => onQrMouseDown?.(e, invoiceQrEl)}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
          cursor: onQrMouseDown ? 'grab' : 'default',
          transform: (qrOffset.x || qrOffset.y) ? `translate(${qrOffset.x}px, ${qrOffset.y}px)` : undefined,
          touchAction: 'none',
        }}
      >
        {makeQr(invoiceQrEl)}
       <div style={{ fontSize: 10, color: '#000000', fontWeight: 800, textAlign: 'center', marginTop: 2 }}>
  {(invoiceLabelEl?.textContent || 'Scan to view invoice').split('\n')[0]}
</div>
{isBilingualActive && (invoiceLabelEl?.arabicContent || qrCaptionInvoiceAr || autoQrLabels.invoice) && (
  <div style={{ fontSize: 9, color: '#000000', fontWeight: 700, fontFamily: getTransFont(lang), direction: rtl ? 'rtl' : 'ltr', textAlign: 'center', marginTop: 2 }}>
    {invoiceLabelEl?.arabicContent || qrCaptionInvoiceAr || autoQrLabels.invoice}
  </div>
)}
      </div>
    )}
    {/* Tracking QR */}
    {trackingQrEl && (
      <div
        onMouseDown={(e) => onQrMouseDown?.(e, trackingQrEl)}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: onQrMouseDown ? 'grab' : 'default' }}
      >
        {makeQr(trackingQrEl)}
      <div style={{ fontSize: 10, color: '#000000', fontWeight: 800, textAlign: 'center', marginTop: 2 }}>
  {(trackingLabelEl?.textContent || 'Scan to track order').split('\n')[0]}
</div>
{isBilingual && (qrCaptionTrackingAr || autoQrLabels.tracking) && (
  <div style={{ fontSize: 9, color: '#000000', fontWeight: 700, fontFamily: getTransFont(lang), direction: rtl ? 'rtl' : 'ltr', textAlign: 'center' }}>
    {qrCaptionTrackingAr || autoQrLabels.tracking}
  </div>
)}
      </div>
    )}
  </div>
)}
    </div>
  );
})()}
  </div>
)}
    </div>
    </div>
  );
}